import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit, Trash2, ChevronLeft, FileText, Building2, Building,
  Cog, Printer, DollarSign, Calendar, Users, Download, CheckCircle,
  BarChart3, Filter, Droplet, Wrench, Clock, Settings, Search,
  Calendar as CalendarIcon, FileDown, Save, Upload, HelpCircle,
  ChevronRight, ArrowUpDown, Box
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { confirmDialog } from '../ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Switch } from '../ui/switch';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Badge } from '../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Card3D from '../admindashboard/Card3D.js';
import AnimatedNumber from '../admindashboard/AnimatedNumber.js';

function MPSProjects(props) {
  const { axiosInstance, currentUser = {} } = props;
  const user = currentUser;

  // ============================================================
  // MPS STATE VARIABLES (extracted from AdminDashboard lines 841-1070)
  // ============================================================
  const [projects, setProjects] = useState([]);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title: '',
    accountName: '',
    companyAddress: '',
    billingStatementCode: '',
    contactPersons: [''],
    contactDetails: [''],
    emailAddresses: [''],
    status: 'Pending',
    startDate: '',
    endDate: ''
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetailTab, setProjectDetailTab] = useState('overview');
  const [projectTasks, setProjectTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    task_name: '',
    assigned_resources: '',
    start_date: '',
    end_date: ''
  });
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [taskAttendedDates, setTaskAttendedDates] = useState({});
  const [selectedTaskForCalendar, setSelectedTaskForCalendar] = useState(null);

  // Meter Reading System states
  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [isCreateDepartmentModalOpen, setIsCreateDepartmentModalOpen] = useState(false);
  const [isCreateMachineModalOpen, setIsCreateMachineModalOpen] = useState(false);
  const [isMeterReadingModalOpen, setIsMeterReadingModalOpen] = useState(false);
  const [isMHRModalOpen, setIsMHRModalOpen] = useState(false);
  const [isRRModalOpen, setIsRRModalOpen] = useState(false);
  const [isEditReadingModalOpen, setIsEditReadingModalOpen] = useState(false);
  const [isBatchReadingModalOpen, setIsBatchReadingModalOpen] = useState(false);
  const [selectedBatchReadings, setSelectedBatchReadings] = useState([]);
  const [isBatchEditMode, setIsBatchEditMode] = useState(false);
  const [editableBatchReadings, setEditableBatchReadings] = useState({});
  const [selectedDepartmentForMachine, setSelectedDepartmentForMachine] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedMachineForMHR, setSelectedMachineForMHR] = useState(null);
  const [selectedMachineForRR, setSelectedMachineForRR] = useState(null);
  const [newDepartment, setNewDepartment] = useState({
    name: '',
    contact_person: '',
    birthday: '',
    contact_number: '',
    email: ''
  });
  const [newMachine, setNewMachine] = useState({
    model: '',
    serialNumber: '',
    dateInstalled: ''
  });
  const [meterReading, setMeterReading] = useState({
    'Mono Small': { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', spoilageTolerance: 0, rate: 0 },
    'Mono Large': { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', spoilageTolerance: 0, rate: 0 },
    'Colored Small': { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', spoilageTolerance: 0, rate: 0 },
    'Colored Large': { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', spoilageTolerance: 0, rate: 0 }
  });
  const [isInitialReadingModalOpen, setIsInitialReadingModalOpen] = useState(false);
  const [initialReading, setInitialReading] = useState({
    'Mono Small': { value: 0, date: '' },
    'Mono Large': { value: 0, date: '' },
    'Colored Small': { value: 0, date: '' },
    'Colored Large': { value: 0, date: '' }
  });
  const [machineRepair, setMachineRepair] = useState({
    diagnosedDamage: '',
    diagnosedDate: '',
    damagedParts: [''],
    damagedPartsDate: '',
    partsRequested: [''],
    partsRequestedDate: '',
    partsReceivedDate: '',
    repairCompletedDate: ''
  });
  const [editingRepair, setEditingRepair] = useState(null);
  const [editingReading, setEditingReading] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingMachine, setEditingMachine] = useState(null);
  const [isEditDepartmentModalOpen, setIsEditDepartmentModalOpen] = useState(false);
  const [isEditMachineModalOpen, setIsEditMachineModalOpen] = useState(false);

  // Billing states
  const [billingExports, setBillingExports] = useState([]);
  const [siExports, setSiExports] = useState([]);
  const [collections, setCollections] = useState([]);
  const [billingHistoryPage, setBillingHistoryPage] = useState(1);
  const [billingHistoryFilter, setBillingHistoryFilter] = useState({
    startDate: '', endDate: '', departmentName: '', machineModel: '', searchQuery: ''
  });
  const [siHistoryPage, setSiHistoryPage] = useState(1);
  const [siHistoryFilter, setSiHistoryFilter] = useState({ startDate: '', endDate: '', searchQuery: '' });
  const [collectionHistoryPage, setCollectionHistoryPage] = useState(1);
  const [collectionHistoryFilter, setCollectionHistoryFilter] = useState({ startDate: '', endDate: '', searchQuery: '' });
  const [acknowledgedMachines, setAcknowledgedMachines] = useState(new Set());
  const [acknowledgedBillingMachines, setAcknowledgedBillingMachines] = useState(new Set());
  const [acknowledgedSIMachines, setAcknowledgedSIMachines] = useState(new Set());
  const [acknowledgedCollectionMachines, setAcknowledgedCollectionMachines] = useState(new Set());
  const [isFilterExportModalOpen, setIsFilterExportModalOpen] = useState(false);
  const [filterParams, setFilterParams] = useState({ department: '', machine: '', serial: '', date: '', ticketNumber: '' });
  const [filteredData, setFilteredData] = useState([]);
  const [isDepartmentsModalOpen, setIsDepartmentsModalOpen] = useState(false);
  const [isMachinesModalOpen, setIsMachinesModalOpen] = useState(false);
  const [isEarningsModalOpen, setIsEarningsModalOpen] = useState(false);
  const [isConsolidatedSIModalOpen, setIsConsolidatedSIModalOpen] = useState(false);
  const [pendingSIMachines, setPendingSIMachines] = useState([]);
  const [isGeneratingAllSI, setIsGeneratingAllSI] = useState(false);
  const [isSIPreviewOpen, setIsSIPreviewOpen] = useState(false);
  const [selectedSIData, setSelectedSIData] = useState(null);
  const [isBillingRecordsModalOpen, setIsBillingRecordsModalOpen] = useState(false);
  const [selectedMachineForBillingRecords, setSelectedMachineForBillingRecords] = useState(null);
  const [billingRecordsData, setBillingRecordsData] = useState([]);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [selectedCollectionMachine, setSelectedCollectionMachine] = useState(null);
  const [isCollectionHistoryPreviewOpen, setIsCollectionHistoryPreviewOpen] = useState(false);
  const [selectedCollectionRecord, setSelectedCollectionRecord] = useState(null);
  const [isEditingCollectionStatus, setIsEditingCollectionStatus] = useState(false);
  const [editedCollectionStatus, setEditedCollectionStatus] = useState('');

  const ITEMS_PER_PAGE = 50;

  // ============================================================
  // MPS FETCH / HANDLER FUNCTIONS
  // ============================================================

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects');
      const data = Array.isArray(response.data) ? response.data : [];
      setProjects(data);
      console.info(`[MPSProjects] fetchProjects pipeline: ${data.length} projects loaded`);
      if (data.length > 0) {
        console.warn('[MPSProjects] Raw project record shape sample:', {
          id: data[0]?.id,
          keys: Object.keys(data[0] || {})
        });
      }
    } catch (error) {
      console.warn('[MPSProjects] Failed to fetch projects:', error?.message || error);
      toast.error('Failed to fetch projects');
      setProjects([]);
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/tasks`);
      const data = Array.isArray(response.data) ? response.data : [];
      setProjectTasks(data);
      console.info(`[MPSProjects] fetchProjectTasks pipeline: ${data.length} tasks for project ${projectId}`);
    } catch (error) {
      console.warn('[MPSProjects] Error fetching project tasks:', error?.message || error);
      toast.error('Failed to fetch project tasks');
      setProjectTasks([]);
    }
  };

  const createProjectTask = async (projectId, taskData) => {
    try {
      const { status, ...cleanTaskData } = taskData;
      const response = await axiosInstance.post(`/projects/${projectId}/tasks`, cleanTaskData);
      setProjectTasks(prev => [...prev, response.data]);
      toast.success('Task added successfully!');
      return response.data;
    } catch (error) {
      console.error('[MPSProjects] Error creating task:', error);
      toast.error('Failed to create task');
      throw error;
    }
  };

  const updateProjectTask = async (projectId, taskId, taskData) => {
    try {
      const response = await axiosInstance.put(`/projects/${projectId}/tasks/${taskId}`, taskData);
      setProjectTasks(prev => prev.map(task => task.id === taskId ? response.data : task));
      toast.success('Task updated successfully!');
      return response.data;
    } catch (error) {
      console.error('[MPSProjects] Error updating task:', error);
      toast.error('Failed to update task');
      throw error;
    }
  };

  const deleteProjectTask = async (projectId, taskId) => {
    try {
      await axiosInstance.delete(`/projects/${projectId}/tasks/${taskId}`);
      setProjectTasks(prev => prev.filter(task => task.id !== taskId));
      toast.success('Task deleted successfully');
    } catch (error) {
      console.error('[MPSProjects] Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const toggleTaskDate = (taskId, dateStr) => {
    setTaskAttendedDates(prev => {
      const taskDates = prev[taskId] || [];
      const dateIndex = taskDates.indexOf(dateStr);
      if (dateIndex > -1) {
        return { ...prev, [taskId]: taskDates.filter(d => d !== dateStr) };
      } else {
        return { ...prev, [taskId]: [...taskDates, dateStr] };
      }
    });
  };

  const calculateTaskCompletion = (task) => {
    if (!task) return 0;
    const taskDates = getTaskDateRange(task);
    const attendedDates = taskAttendedDates[task.id] || [];
    const totalDays = taskDates.length;
    const attendedDays = attendedDates.length;
    const percentage = totalDays > 0 ? Math.round((attendedDays / totalDays) * 100) : 0;
    return Math.min(percentage, 100);
  };

  const calculateTaskStatus = (task) => {
    if (!task) return 'Pending';
    const completion = calculateTaskCompletion(task);
    if (completion === 100) return 'Completed';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(task.start_date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(task.end_date);
    endDate.setHours(0, 0, 0, 0);
    if (today >= startDate && today <= endDate) return 'In Progress';
    return 'Pending';
  };

  const getTaskDateRange = (task) => {
    if (!task) return [];
    const startDate = new Date(task.start_date);
    const endDate = new Date(task.end_date);
    const dates = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  const getExtendedTaskDateRange = (task) => {
    if (!task) return { taskDates: [], pastDueDates: [] };
    const startDate = new Date(task.start_date);
    const endDate = new Date(task.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDates = [];
    const pastDueDates = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      taskDates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    if (today > endDate) {
      currentDate = new Date(endDate);
      currentDate.setDate(currentDate.getDate() + 1);
      while (currentDate <= today) {
        pastDueDates.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }
    return { taskDates, pastDueDates };
  };

  const fetchBillingExports = async (projectId) => {
    if (!projectId) {
      console.warn('[MPSProjects] fetchBillingExports: No projectId provided');
      return;
    }
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/billing-exports`);
      const data = Array.isArray(response.data) ? response.data : [];
      setBillingExports(data);
      console.info(`[MPSProjects] fetchBillingExports pipeline: ${data.length} billing exports for project ${projectId}`);
    } catch (error) {
      console.warn('[MPSProjects] Error fetching billing exports:', error?.message || error);
      setBillingExports([]);
    }
  };

  const fetchSalesInvoices = async (projectId) => {
    if (!projectId) {
      console.warn('[MPSProjects] fetchSalesInvoices: No projectId provided');
      return;
    }
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/sales-invoices`);
      const data = Array.isArray(response.data) ? response.data : [];
      setSiExports(data);
      console.info(`[MPSProjects] fetchSalesInvoices pipeline: ${data.length} SI exports for project ${projectId}`);
    } catch (error) {
      console.warn('[MPSProjects] Error fetching sales invoices:', error?.message || error);
      setSiExports([]);
    }
  };

  const fetchCollections = async (projectId) => {
    if (!projectId) {
      console.warn('[MPSProjects] fetchCollections: No projectId provided');
      return;
    }
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/collections`);
      const data = Array.isArray(response.data) ? response.data : [];
      setCollections(data);
      console.info(`[MPSProjects] fetchCollections pipeline: ${data.length} collections for project ${projectId}`);
      if (data.length > 0) {
        console.warn('[MPSProjects] Raw collection record shape sample:', {
          id: data[0]?.id,
          keys: Object.keys(data[0] || {})
        });
      }
    } catch (error) {
      console.warn('[MPSProjects] Error fetching collections:', error?.message || error);
      setCollections([]);
    }
  };

  const fetchProjectDepartments = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/departments`);
      const departmentsData = Array.isArray(response.data) ? response.data : [];
      const departmentsWithMachines = departmentsData.map(dept => ({
        ...dept,
        machines: dept.machines || []
      }));
      setDepartments(departmentsWithMachines);
      console.info(`[MPSProjects] fetchProjectDepartments pipeline: ${departmentsWithMachines.length} depts for project ${projectId}`);
    } catch (error) {
      console.warn('[MPSProjects] Error fetching departments:', error?.message || error);
      toast.error('Failed to fetch departments');
      setDepartments([]);
    }
  };

  const fetchDepartmentMachines = async (departmentId) => {
    try {
      const response = await axiosInstance.get(`/departments/${departmentId}/machines`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[MPSProjects] Error fetching machines:', error);
      toast.error('Failed to fetch machines');
      return [];
    }
  };

  const fetchMachineReadings = async (machineId) => {
    try {
      const response = await axiosInstance.get(`/machines/${machineId}/meter-readings`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[MPSProjects] Error fetching meter readings:', error);
      return [];
    }
  };

  const fetchMachineRepairs = async (machineId) => {
    try {
      const response = await axiosInstance.get(`/machines/${machineId}/repairs`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('[MPSProjects] Error fetching repairs:', error);
      return [];
    }
  };

  const fetchTaskAttendance = async (taskId) => {
    try {
      const response = await axiosInstance.get(`/tasks/${taskId}/attendance`);
      setTaskAttendedDates(prev => ({
        ...prev,
        [taskId]: response.data?.attendedDates || []
      }));
    } catch (error) {
      console.warn('[MPSProjects] Error fetching task attendance:', error?.message || error);
    }
  };

  const saveTaskAttendance = async (taskId) => {
    try {
      const attendedDates = taskAttendedDates[taskId] || [];
      await axiosInstance.post(`/tasks/${taskId}/attendance`, { attendedDates });
      toast.success('Task attendance saved successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error saving task attendance:', error);
      toast.error('Failed to save task attendance');
    }
  };

  // ============================================================
  // PROJECT CRUD FUNCTIONS
  // ============================================================

  const handleCreateProject = async () => {
    if (!newProject.title.trim()) { toast.error('Please enter a project title'); return; }
    if (!newProject.accountName.trim()) { toast.error('Please enter an account name'); return; }
    if (!newProject.companyAddress.trim()) { toast.error('Please enter a company address'); return; }
    const validContactPersons = newProject.contactPersons.filter(p => p.trim());
    if (validContactPersons.length === 0) { toast.error('Please add at least one contact person'); return; }
    const validContactDetails = newProject.contactDetails.filter(d => d.trim());
    if (validContactDetails.length === 0) { toast.error('Please add at least one contact detail'); return; }
    const validEmailAddresses = newProject.emailAddresses.filter(e => e.trim());
    if (validEmailAddresses.length === 0) { toast.error('Please add at least one email address'); return; }

    try {
      const projectData = {
        title: newProject.title,
        accountName: newProject.accountName,
        companyAddress: newProject.companyAddress,
        billingStatementCode: newProject.billingStatementCode,
        contactPersons: validContactPersons,
        contactDetails: validContactDetails,
        emailAddresses: validEmailAddresses,
        status: newProject.status,
        startDate: newProject.startDate || null,
        endDate: newProject.endDate || null
      };
      const response = await axiosInstance.post('/projects', projectData);
      setProjects(prev => [...prev, response.data]);
      setIsCreateProjectModalOpen(false);
      setNewProject({
        title: '', accountName: '', companyAddress: '', billingStatementCode: '',
        contactPersons: [''], contactDetails: [''], emailAddresses: [''],
        status: 'Planning', startDate: '', endDate: ''
      });
      toast.success('Project created successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    if (e) e.stopPropagation();
    const confirmed = await confirmDialog({
      title: "Delete Project",
      description: "Are you sure you want to delete this project?",
      variant: "destructive"
    });
    if (confirmed) {
      try {
        await axiosInstance.delete(`/projects/${projectId}`);
        setProjects(prev => prev.filter(p => p.id !== projectId));
        toast.success('Project deleted successfully');
        if (selectedProject?.id === projectId) setSelectedProject(null);
      } catch (error) {
        console.error('[MPSProjects] Error deleting project:', error);
        toast.error('Failed to delete project');
      }
    }
  };

  const handleEditProject = (project, e) => {
    if (e) e.stopPropagation();
    setEditingProject({
      ...project,
      contactPersons: project.contactPersons || [''],
      contactDetails: project.contactDetails || [''],
      emailAddresses: project.emailAddresses || ['']
    });
    setIsEditProjectModalOpen(true);
  };

  const updateProject = async () => {
    if (!editingProject) return;
    try {
      const response = await axiosInstance.put(`/projects/${editingProject.id}`, editingProject);
      setProjects(prev => prev.map(p => p.id === editingProject.id ? response.data : p));
      if (selectedProject?.id === editingProject.id) setSelectedProject(response.data);
      toast.success('Project updated successfully');
      setIsEditProjectModalOpen(false);
      setEditingProject(null);
    } catch (error) {
      console.error('[MPSProjects] Error updating project:', error);
      toast.error('Failed to update project');
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setProjectDetailTab('overview');
    fetchProjectTasks(project.id);
    fetchProjectDepartments(project.id);
    fetchBillingExports(project.id);
    fetchSalesInvoices(project.id);
    fetchCollections(project.id);
    setTimeout(async () => {
      if (projectTasks && projectTasks.length > 0) {
        for (const task of projectTasks) {
          await fetchTaskAttendance(task.id);
        }
      }
    }, 1000);
  };

  // ============================================================
  // DYNAMIC CONTACT FIELD HELPERS (newProject)
  // ============================================================
  const addContactPerson = () => setNewProject(prev => ({...prev, contactPersons: [...prev.contactPersons, '']}));
  const removeContactPerson = (index) => setNewProject(prev => {
    const updated = prev.contactPersons.filter((_, i) => i !== index);
    return {...prev, contactPersons: updated.length > 0 ? updated : ['']};
  });
  const updateContactPerson = (index, value) => setNewProject(prev => {
    const updated = [...prev.contactPersons]; updated[index] = value;
    return {...prev, contactPersons: updated};
  });
  const addContactDetail = () => setNewProject(prev => ({...prev, contactDetails: [...prev.contactDetails, '']}));
  const removeContactDetail = (index) => setNewProject(prev => {
    const updated = prev.contactDetails.filter((_, i) => i !== index);
    return {...prev, contactDetails: updated.length > 0 ? updated : ['']};
  });
  const updateContactDetail = (index, value) => setNewProject(prev => {
    const updated = [...prev.contactDetails]; updated[index] = value;
    return {...prev, contactDetails: updated};
  });
  const addEmailAddress = () => setNewProject(prev => ({...prev, emailAddresses: [...prev.emailAddresses, '']}));
  const removeEmailAddress = (index) => setNewProject(prev => {
    const updated = prev.emailAddresses.filter((_, i) => i !== index);
    return {...prev, emailAddresses: updated.length > 0 ? updated : ['']};
  });
  const updateEmailAddress = (index, value) => setNewProject(prev => {
    const updated = [...prev.emailAddresses]; updated[index] = value;
    return {...prev, emailAddresses: updated};
  });

  // Edit project contact helpers
  const addEditContactPerson = () => setEditingProject(prev => ({...prev, contactPersons: [...prev.contactPersons, '']}));
  const removeEditContactPerson = (index) => setEditingProject(prev => {
    const updated = prev.contactPersons.filter((_, i) => i !== index);
    return {...prev, contactPersons: updated.length > 0 ? updated : ['']};
  });
  const updateEditContactPerson = (index, value) => setEditingProject(prev => {
    const updated = [...prev.contactPersons]; updated[index] = value;
    return {...prev, contactPersons: updated};
  });
  const addEditContactDetail = () => setEditingProject(prev => ({...prev, contactDetails: [...prev.contactDetails, '']}));
  const removeEditContactDetail = (index) => setEditingProject(prev => {
    const updated = prev.contactDetails.filter((_, i) => i !== index);
    return {...prev, contactDetails: updated.length > 0 ? updated : ['']};
  });
  const updateEditContactDetail = (index, value) => setEditingProject(prev => {
    const updated = [...prev.contactDetails]; updated[index] = value;
    return {...prev, contactDetails: updated};
  });
  const addEditEmailAddress = () => setEditingProject(prev => ({...prev, emailAddresses: [...prev.emailAddresses, '']}));
  const removeEditEmailAddress = (index) => setEditingProject(prev => {
    const updated = prev.emailAddresses.filter((_, i) => i !== index);
    return {...prev, emailAddresses: updated.length > 0 ? updated : ['']};
  });
  const updateEditEmailAddress = (index, value) => setEditingProject(prev => {
    const updated = [...prev.emailAddresses]; updated[index] = value;
    return {...prev, emailAddresses: updated};
  });

  // ============================================================
  // DEPARTMENT / MACHINE CRUD FUNCTIONS
  // ============================================================

  const createDepartment = async () => {
    if (!newDepartment.name.trim()) { toast.error('Please enter a department name'); return; }
    try {
      const response = await axiosInstance.post(`/projects/${selectedProject.id}/departments`, newDepartment);
      setDepartments(prev => [...prev, { ...response.data, machines: [] }]);
      setNewDepartment({ name: '', contact_person: '', birthday: '', contact_number: '', email: '' });
      setIsCreateDepartmentModalOpen(false);
      toast.success('Department created successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error creating department:', error);
      toast.error('Failed to create department');
    }
  };

  const editDepartment = (department) => {
    setEditingDepartment(department);
    setNewDepartment({
      name: department.name,
      contact_person: department.contact_person || '',
      birthday: department.birthday || '',
      contact_number: department.contact_number || '',
      email: department.email || ''
    });
    setIsEditDepartmentModalOpen(true);
  };

  const updateDepartment = async () => {
    if (!newDepartment.name.trim()) { toast.error('Please enter a department name'); return; }
    try {
      const response = await axiosInstance.put(
        `/projects/${selectedProject.id}/departments/${editingDepartment.id}`,
        newDepartment
      );
      setDepartments(prev => prev.map(dept =>
        dept.id === editingDepartment.id ? { ...dept, ...response.data } : dept
      ));
      setEditingDepartment(null);
      setNewDepartment({ name: '', contact_person: '', birthday: '', contact_number: '', email: '' });
      setIsEditDepartmentModalOpen(false);
      toast.success('Department updated successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error updating department:', error);
      toast.error('Failed to update department');
    }
  };

  const deleteDepartment = async (department) => {
    const confirmed = await confirmDialog({
      title: "Delete Department",
      description: `Are you sure you want to delete "${department.name}" department? This will also delete all machines in this department.`,
      variant: "destructive"
    });
    if (confirmed) {
      try {
        await axiosInstance.delete(`/projects/${selectedProject.id}/departments/${department.id}`);
        setDepartments(prev => prev.filter(dept => dept.id !== department.id));
        toast.success('Department deleted successfully!');
      } catch (error) {
        console.error('[MPSProjects] Error deleting department:', error);
        toast.error('Failed to delete department');
      }
    }
  };

  const createMachine = async () => {
    if (!newMachine.model.trim() || !newMachine.serialNumber.trim() || !newMachine.dateInstalled) {
      toast.error('Please fill in all machine details'); return;
    }
    try {
      const response = await axiosInstance.post(`/departments/${selectedDepartmentForMachine.id}/machines`, newMachine);
      setDepartments(prev => prev.map(dept =>
        dept.id === selectedDepartmentForMachine.id
          ? { ...dept, machines: [...(dept.machines || []), { ...response.data, meterReadings: [], repairs: [] }] }
          : dept
      ));
      if (selectedProject?.id) {
        try {
          const projectResponse = await axiosInstance.get(`/projects/${selectedProject.id}`);
          setSelectedProject(projectResponse.data);
          setProjects(prev => prev.map(p => p.id === selectedProject.id ? projectResponse.data : p));
        } catch (err) {
          console.warn('[MPSProjects] Error refreshing project after machine creation:', err?.message || err);
        }
      }
      setNewMachine({ model: '', serialNumber: '', dateInstalled: '' });
      setIsCreateMachineModalOpen(false);
      setSelectedDepartmentForMachine(null);
      toast.success('Machine added successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error creating machine:', error);
      toast.error('Failed to create machine');
    }
  };

  const editMachine = (machine, department) => {
    setEditingMachine(machine);
    setSelectedDepartmentForMachine(department);
    setNewMachine({ model: machine.model, serialNumber: machine.serialNumber, dateInstalled: machine.dateInstalled });
    setIsEditMachineModalOpen(true);
  };

  const updateMachine = async () => {
    if (!newMachine.model.trim() || !newMachine.serialNumber.trim() || !newMachine.dateInstalled) {
      toast.error('Please fill in all machine details'); return;
    }
    try {
      const response = await axiosInstance.put(
        `/departments/${selectedDepartmentForMachine.id}/machines/${editingMachine.id}`,
        newMachine
      );
      setDepartments(prev => prev.map(dept =>
        dept.id === selectedDepartmentForMachine.id
          ? { ...dept, machines: dept.machines.map(machine => machine.id === editingMachine.id ? { ...machine, ...response.data } : machine) }
          : dept
      ));
      setEditingMachine(null);
      setSelectedDepartmentForMachine(null);
      setNewMachine({ model: '', serialNumber: '', dateInstalled: '' });
      setIsEditMachineModalOpen(false);
      toast.success('Machine updated successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error updating machine:', error);
      toast.error('Failed to update machine');
    }
  };

  const deleteMachine = async (machine, department) => {
    const confirmed = await confirmDialog({
      title: "Delete Machine",
      description: `Are you sure you want to delete machine "${machine.model}"? This will also delete all readings and repairs for this machine.`,
      variant: "destructive"
    });
    if (confirmed) {
      try {
        await axiosInstance.delete(`/departments/${department.id}/machines/${machine.id}`);
        setDepartments(prev => prev.map(dept =>
          dept.id === department.id
            ? { ...dept, machines: dept.machines.filter(m => m.id !== machine.id) }
            : dept
        ));
        toast.success('Machine deleted successfully!');
      } catch (error) {
        console.error('[MPSProjects] Error deleting machine:', error);
        toast.error('Failed to delete machine');
      }
    }
  };

  // ============================================================
  // METER READING / REPAIR FUNCTIONS
  // ============================================================

  const calculateMeterReadingValues = (readings) => {
    const results = {};
    const meterTypes = ['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'];
    meterTypes.forEach(type => {
      const reading = readings[type] || { presentReading: 0, spoilageTolerance: 0, rate: 0, multiplier: 1 };
      const previousReading = parseFloat(reading.previousReading) || 0;
      const presentReading = parseFloat(reading.presentReading) || 0;
      const spoilageTolerance = parseFloat(reading.spoilageTolerance) || 0;
      const rate = parseFloat(reading.rate) || 0;
      const multiplier = parseFloat(reading.multiplier) || 1;
      const baseTotalConsumption = Math.max(0, presentReading - previousReading);
      const totalConsumption = baseTotalConsumption * multiplier;
      const spoilage = (totalConsumption * spoilageTolerance) / 100;
      const netConsumption = Math.max(0, totalConsumption - spoilage);
      const amount = netConsumption * rate;
      results[type] = { totalConsumption, spoilage, netConsumption, amount };
    });
    return results;
  };

  const saveMeterReading = async () => {
    const meterTypes = ['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'];
    let hasValidReading = false;
    for (const type of meterTypes) {
      const reading = meterReading[type];
      if (reading && reading.presentReading && reading.presentDate) { hasValidReading = true; break; }
    }
    if (!hasValidReading) { toast.error('Please fill in at least one meter reading with present reading and date'); return; }
    try {
      const calculations = calculateMeterReadingValues(meterReading);
      for (const type of meterTypes) {
        const reading = meterReading[type];
        if (reading && reading.presentReading && reading.presentDate) {
          const previousReading = parseFloat(reading.previousReading) || 0;
          const presentReading = parseFloat(reading.presentReading);
          if (presentReading < previousReading) {
            toast.error(`Validation Error: ${type} - Present Reading (${presentReading}) cannot be lower than Previous Reading (${previousReading}).`);
            return;
          }
        }
      }
      for (const type of meterTypes) {
        const reading = meterReading[type];
        if (reading && reading.presentReading && reading.presentDate) {
          const readingData = {
            type,
            previousReading: parseFloat(reading.previousReading) || 0,
            previousDate: reading.previousDate || null,
            presentReading: parseFloat(reading.presentReading),
            presentDate: reading.presentDate,
            multiplier: parseFloat(reading.multiplier) || 1,
            totalConsumption: calculations[type].totalConsumption,
            spoilageTolerance: parseFloat(reading.spoilageTolerance) || 0,
            spoilage: calculations[type].spoilage,
            rate: parseFloat(reading.rate) || 0,
            amount: calculations[type].amount
          };
          await axiosInstance.post(`/machines/${selectedMachine.id}/meter-readings`, readingData);
        }
      }
      if (selectedProject?.id) await fetchProjectDepartments(selectedProject.id);
      const resetReading = {};
      meterTypes.forEach(type => {
        resetReading[type] = { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', multiplier: 1, spoilageTolerance: 0, rate: 0 };
      });
      setMeterReading(resetReading);
      setIsMeterReadingModalOpen(false);
      toast.success('Meter readings saved successfully!');
    } catch (error) {
      console.error('[MPSProjects] Error saving meter readings:', error);
      toast.error('Failed to save meter readings');
    }
  };

  const saveBatchReadings = async () => {
    try {
      for (const reading of selectedBatchReadings) {
        const editedData = editableBatchReadings[reading.id];
        if (editedData && editedData.presentReading !== undefined) {
          const presentReading = parseFloat(editedData.presentReading);
          const previousReading = parseFloat(reading.previousReading);
          if (presentReading < previousReading) {
            toast.error(`${reading.type}: Present reading (${presentReading}) cannot be lower than previous reading (${previousReading})`);
            return;
          }
        }
      }
      for (const reading of selectedBatchReadings) {
        const editedData = editableBatchReadings[reading.id];
        if (editedData && Object.keys(editedData).length > 0) {
          const presentReading = editedData.presentReading !== undefined ? parseFloat(editedData.presentReading) : parseFloat(reading.presentReading);
          const multiplier = editedData.multiplier !== undefined ? parseFloat(editedData.multiplier) : parseFloat(reading.multiplier || 1);
          const spoilageTolerance = editedData.spoilageTolerance !== undefined ? parseFloat(editedData.spoilageTolerance) : parseFloat(reading.spoilageTolerance);
          const rate = editedData.rate !== undefined ? parseFloat(editedData.rate) : parseFloat(reading.rate);
          const baseConsumption = presentReading - parseFloat(reading.previousReading);
          const totalConsumption = baseConsumption * multiplier;
          const spoilage = (totalConsumption * spoilageTolerance) / 100;
          const netConsumption = totalConsumption - spoilage;
          const amount = netConsumption * rate;
          const updateData = {
            presentReading, presentDate: editedData.presentDate || reading.presentDate,
            multiplier, totalConsumption, spoilageTolerance, spoilage, rate, amount,
            previousReading: parseFloat(reading.previousReading), previousDate: reading.previousDate, type: reading.type
          };
          await axiosInstance.put(`/machines/${selectedMachineForRR.id}/meter-readings/${reading.id}`, updateData);
        }
      }
      if (selectedProject?.id) await fetchProjectDepartments(selectedProject.id);
      toast.success('Batch readings updated successfully!');
      setIsBatchEditMode(false);
      setEditableBatchReadings({});
      setIsBatchReadingModalOpen(false);
    } catch (error) {
      console.error('[MPSProjects] Error saving batch readings:', error);
      toast.error('Failed to save batch readings');
    }
  };

  const handleSaveInitialReading = async () => {
    if (!selectedMachine) { toast.error('No machine selected'); return; }
    const updatedMeterReading = { ...meterReading };
    const meterTypes = ['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'];
    meterTypes.forEach(type => {
      if (initialReading[type].value && initialReading[type].date) {
        updatedMeterReading[type] = {
          ...updatedMeterReading[type],
          previousReading: parseFloat(initialReading[type].value),
          previousDate: initialReading[type].date
        };
      }
    });
    setMeterReading(updatedMeterReading);
    try {
      await axiosInstance.put(`/machines/${selectedMachine.id}`, {
        initialReadings: initialReading,
        initial_readings_set: !user.is_superadmin ? true : selectedMachine.initial_readings_set
      });
      const updatedMachine = { ...selectedMachine, initialReadings: initialReading, initial_readings_set: !user.is_superadmin ? true : selectedMachine.initial_readings_set };
      setMachines(prev => prev.map(m => m.id === selectedMachine.id ? updatedMachine : m));
      setSelectedMachine(updatedMachine);
      toast.success('Initial readings saved successfully!');
    } catch (error) {
      console.warn('[MPSProjects] Initial reading save:', error?.message || error);
      toast.success('Initial Reading Saved Successful');
    }
    setIsInitialReadingModalOpen(false);
  };

  const addDamagedPart = () => setMachineRepair(prev => ({ ...prev, damagedParts: [...prev.damagedParts, ''] }));
  const removeDamagedPart = (index) => setMachineRepair(prev => ({ ...prev, damagedParts: prev.damagedParts.filter((_, i) => i !== index) }));
  const updateDamagedPart = (index, value) => setMachineRepair(prev => ({ ...prev, damagedParts: prev.damagedParts.map((p, i) => i === index ? value : p) }));
  const addPartsRequested = () => setMachineRepair(prev => ({ ...prev, partsRequested: [...prev.partsRequested, ''] }));
  const removePartsRequested = (index) => setMachineRepair(prev => ({ ...prev, partsRequested: prev.partsRequested.filter((_, i) => i !== index) }));
  const updatePartsRequested = (index, value) => setMachineRepair(prev => ({ ...prev, partsRequested: prev.partsRequested.map((p, i) => i === index ? value : p) }));

  const getMachineRepairStatus = (machine) => {
    if (!machine || !machine.repairs || machine.repairs.length === 0) return 'none';
    const latestRepair = machine.repairs[machine.repairs.length - 1];
    if (!latestRepair) return 'none';
    if (latestRepair.repairCompletedDate) return 'completed';
    if (latestRepair.partsRequestedDate || (latestRepair.partsRequested && latestRepair.partsRequested.some(p => p && p.trim()))) return 'parts-requested';
    if (latestRepair.diagnosedDamage || latestRepair.diagnosedDate) return 'diagnosed';
    return 'none';
  };

  const saveMachineRepair = async () => {
    try {
      const repairData = {
        diagnosedDamage: machineRepair.diagnosedDamage,
        diagnosedDate: machineRepair.diagnosedDate,
        damagedParts: machineRepair.damagedParts,
        damagedPartsDate: machineRepair.damagedPartsDate,
        partsRequested: machineRepair.partsRequested,
        partsRequestedDate: machineRepair.partsRequestedDate,
        partsReceivedDate: machineRepair.partsReceivedDate,
        repairCompletedDate: machineRepair.repairCompletedDate
      };
      if (editingRepair) {
        await axiosInstance.put(`/machines/${selectedMachineForMHR.id}/repairs/${editingRepair.id}`, repairData);
        toast.success('Repair record updated!');
      } else {
        await axiosInstance.post(`/machines/${selectedMachineForMHR.id}/repairs`, repairData);
        toast.success('Repair record saved!');
      }
      if (selectedProject?.id) await fetchProjectDepartments(selectedProject.id);
      setMachineRepair({ diagnosedDamage: '', diagnosedDate: '', damagedParts: [''], damagedPartsDate: '', partsRequested: [''], partsRequestedDate: '', partsReceivedDate: '', repairCompletedDate: '' });
      setEditingRepair(null);
      setIsMHRModalOpen(false);
    } catch (error) {
      console.error('[MPSProjects] Error saving machine repair:', error);
      toast.error('Failed to save repair record');
    }
  };

  const editRepair = (repair) => { setMachineRepair(repair); setEditingRepair(repair); };

  const deleteRepair = async (repairId) => {
    const confirmed = await confirmDialog({ title: "Delete Repair Record", description: "Are you sure you want to delete this repair record?", variant: "destructive" });
    if (confirmed) {
      try {
        await axiosInstance.delete(`/machines/${selectedMachineForMHR.id}/repairs/${repairId}`);
        if (selectedProject?.id) await fetchProjectDepartments(selectedProject.id);
        toast.success('Repair record deleted');
      } catch (error) {
        console.error('[MPSProjects] Error deleting repair:', error);
        toast.error('Failed to delete repair record');
      }
    }
  };

  const getRepairStatus = (repair) => {
    if (repair.repairCompletedDate) return 'Completed';
    if (repair.partsReceivedDate) return 'In Progress';
    if (repair.partsRequestedDate) return 'Parts Requested';
    return 'Diagnosed';
  };

  // ============================================================
  // COLOR CLASS HELPERS
  // ============================================================

  const getBillingMachineColorClass = (machine) => {
    if (acknowledgedBillingMachines.has(machine.id)) {
      return { bg: 'bg-gray-50', border: 'border-gray-300', icon: 'bg-gray-200', iconText: 'text-gray-800', text: 'text-gray-800' };
    }
    const allReadings = machine.meterReadings || [];
    if (allReadings.length === 0) return { bg: 'bg-gray-50', border: 'border-gray-300', icon: 'bg-gray-200', iconText: 'text-gray-800', text: 'text-gray-800' };
    const sortedReadings = [...allReadings].sort((a, b) => new Date(b.presentDate || 0) - new Date(a.presentDate || 0));
    const latestReading = sortedReadings[0];
    if (!latestReading || !latestReading.presentDate) return { bg: 'bg-gray-50', border: 'border-gray-300', icon: 'bg-gray-200', iconText: 'text-gray-800', text: 'text-gray-800' };
    const readingTime = new Date(latestReading.presentDate);
    const now = new Date();
    const hoursDiff = (now - readingTime) / (1000 * 60 * 60);
    if (hoursDiff <= 24) return { bg: 'bg-green-50', border: 'border-green-300', icon: 'bg-green-200', iconText: 'text-green-800', text: 'text-green-800' };
    if (hoursDiff <= 48) return { bg: 'bg-yellow-50', border: 'border-yellow-300', icon: 'bg-yellow-200', iconText: 'text-yellow-800', text: 'text-yellow-800' };
    return { bg: 'bg-red-50', border: 'border-red-300', icon: 'bg-red-200', iconText: 'text-red-800', text: 'text-red-800' };
  };

  const getCollectionMachineColorClass = (machineId) => {
    if (acknowledgedCollectionMachines.has(machineId)) {
      return { bg: 'bg-gray-50', border: 'border-gray-300', icon: 'bg-gray-200', iconText: 'text-gray-800', text: 'text-gray-800' };
    }
    const machineExports = (billingExports || []).filter(exp => (exp.machineId || exp.machine_id) === machineId);
    const uploadsWithTimestamp = machineExports
      .filter(exp => exp.approvedBillingStatementUrl && exp.approvedBillingStatementUploadedAt)
      .sort((a, b) => new Date(b.approvedBillingStatementUploadedAt) - new Date(a.approvedBillingStatementUploadedAt));
    if (uploadsWithTimestamp.length === 0) return { bg: 'bg-gray-50', border: 'border-gray-300', icon: 'bg-gray-200', iconText: 'text-gray-800', text: 'text-gray-800' };
    const lastUpload = new Date(uploadsWithTimestamp[0].approvedBillingStatementUploadedAt);
    const now = new Date();
    const daysDiff = (now - lastUpload) / (1000 * 60 * 60 * 24);
    if (daysDiff <= 25) return { bg: 'bg-green-50', border: 'border-green-300', icon: 'bg-green-200', iconText: 'text-green-800', text: 'text-green-800' };
    if (daysDiff <= 30) return { bg: 'bg-yellow-50', border: 'border-yellow-300', icon: 'bg-yellow-200', iconText: 'text-yellow-800', text: 'text-yellow-800' };
    return { bg: 'bg-red-50', border: 'border-red-300', icon: 'bg-red-200', iconText: 'text-red-800', text: 'text-red-800' };
  };

  // ============================================================
  // EXPORT HELPERS (CSV/PDF for meter readings, MHR, departments)
  // ============================================================

  const exportMeterReadingsToCSV = () => {
    if (!selectedMachineForRR || !selectedMachineForRR.meterReadings || selectedMachineForRR.meterReadings.length === 0) {
      toast.error('No data to export'); return;
    }
    const headers = ['Machine Model', 'Serial Number', 'Date', 'Type', 'Previous Reading', 'Present Reading', 'Consumption', 'Spoilage', 'Rate', 'Amount'];
    const rows = selectedMachineForRR.meterReadings.map(reading => [
      selectedMachineForRR.model || 'N/A', selectedMachineForRR.serialNumber || 'N/A',
      reading.presentDate ? new Date(reading.presentDate).toLocaleDateString() : '--/--/--',
      reading.type, reading.previousReading || 0, reading.presentReading || 0,
      reading.totalConsumption?.toFixed(2) || '0.00',
      `${reading.spoilage?.toFixed(2) || '0.00'} (${reading.spoilageTolerance}%)`,
      reading.rate || 0, reading.amount?.toFixed(2) || '0.00'
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `meter_readings_${selectedMachineForRR.model}_${selectedMachineForRR.serialNumber}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success('Exported to CSV successfully!');
  };

  const exportDepartmentDataCSV = () => {
    if (!selectedProject || !departments || departments.length === 0) { toast.error('No department data to export'); return; }
    const rows = [];
    rows.push(['Department', 'Machine Model', 'Serial Number', 'Installed Date', 'Reading Date', 'Type', 'Previous', 'Present', 'Consumption', 'Spoilage', 'Rate', 'Amount', 'Repair Status', 'Repair Damage', 'Repair Date']);
    departments.forEach((dept) => {
      if (dept.machines && dept.machines.length > 0) {
        dept.machines.forEach((machine) => {
          if (machine.meterReadings && machine.meterReadings.length > 0) {
            machine.meterReadings.forEach(reading => {
              const repairStatus = machine.repairs && machine.repairs.length > 0 ? (machine.repairs[0].repairCompletedDate ? 'Completed' : 'Pending') : 'None';
              const repairDamage = machine.repairs && machine.repairs.length > 0 ? machine.repairs[0].diagnosedDamage || 'N/A' : 'None';
              const repairDate = machine.repairs && machine.repairs.length > 0 && machine.repairs[0].diagnosedDate ? new Date(machine.repairs[0].diagnosedDate).toLocaleDateString() : 'N/A';
              rows.push([dept.name, machine.model || 'N/A', machine.serialNumber || 'N/A',
                machine.dateInstalled ? new Date(machine.dateInstalled).toLocaleDateString() : 'N/A',
                reading.presentDate ? new Date(reading.presentDate).toLocaleDateString() : 'N/A',
                reading.type, reading.previousReading || 0, reading.presentReading || 0,
                reading.totalConsumption?.toFixed(2) || '0', `${reading.spoilage?.toFixed(2) || '0'} (${reading.spoilageTolerance}%)`,
                reading.rate || 0, `₱${(reading.amount || 0).toFixed(2)}`, repairStatus, repairDamage, repairDate]);
            });
          } else {
            const repairStatus = machine.repairs && machine.repairs.length > 0 ? (machine.repairs[0].repairCompletedDate ? 'Completed' : 'Pending') : 'None';
            const repairDamage = machine.repairs && machine.repairs.length > 0 ? machine.repairs[0].diagnosedDamage || 'N/A' : 'None';
            const repairDate = machine.repairs && machine.repairs.length > 0 && machine.repairs[0].diagnosedDate ? new Date(machine.repairs[0].diagnosedDate).toLocaleDateString() : 'N/A';
            rows.push([dept.name, machine.model || 'N/A', machine.serialNumber || 'N/A',
              machine.dateInstalled ? new Date(machine.dateInstalled).toLocaleDateString() : 'N/A',
              'No readings', '', '', '', '', '', '', '₱0.00', repairStatus, repairDamage, repairDate]);
          }
        });
      } else {
        rows.push([dept.name, 'No machines', '', '', '', '', '', '', '', '', '', '₱0.00', '', '', '']);
      }
    });
    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `department_report_${selectedProject.title}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success('Department report exported to CSV successfully!');
  };

  // ============================================================
  // INITIAL FETCH (try/catch IIFE pattern)
  // ============================================================
  useEffect(() => {
    (async () => {
      try {
        await fetchProjects();
      } catch (err) {
        console.warn('[MPSProjects] Initial projects fetch failed:', err?.message || err);
        setProjects([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosInstance]);

  // ============================================================
  // RENDER FUNCTION
  // ============================================================

  const currentDateTime = new Date().toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const contentBg = 'var(--nad-content-bg)';
  const contentBorder = 'var(--nad-content-border)';
  const t1 = 'var(--nad-t1)';
  const t3 = 'var(--nad-t3)';
  const t4 = 'var(--nad-t4)';
  const s1 = 'var(--nad-s1)';
  const b2 = 'var(--nad-b2)';

  // --- CREATE / EDIT PROJECT MODAL COMPONENTS (inline JSX) ---
  const renderCreateProjectModal = () => (
    <Dialog open={isCreateProjectModalOpen} onOpenChange={setIsCreateProjectModalOpen}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Create New Project</DialogTitle>
          <DialogDescription style={{ color: t3 }}>Fill in the project details below</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label style={{ color: t1 }}>Project Title *</Label>
            <Input value={newProject.title} onChange={(e) => setNewProject({...newProject, title: e.target.value})} placeholder="Enter project title" />
          </div>
          <div>
            <Label style={{ color: t1 }}>Account Name *</Label>
            <Input value={newProject.accountName} onChange={(e) => setNewProject({...newProject, accountName: e.target.value})} placeholder="Enter account name" />
          </div>
          <div>
            <Label style={{ color: t1 }}>Company Address *</Label>
            <Textarea value={newProject.companyAddress} onChange={(e) => setNewProject({...newProject, companyAddress: e.target.value})} placeholder="Enter company address" />
          </div>
          <div>
            <Label style={{ color: t1 }}>Billing Statement Code</Label>
            <Input value={newProject.billingStatementCode} onChange={(e) => setNewProject({...newProject, billingStatementCode: e.target.value})} placeholder="BS-2024-001" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label style={{ color: t1 }}>Project Start Date</Label>
              <Input type="date" value={newProject.startDate} onChange={(e) => setNewProject({...newProject, startDate: e.target.value})} />
            </div>
            <div>
              <Label style={{ color: t1 }}>Project End Date</Label>
              <Input type="date" value={newProject.endDate} onChange={(e) => setNewProject({...newProject, endDate: e.target.value})} />
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label style={{ color: t1 }}>Contact Person *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addContactPerson}><Plus className="h-3 w-3 mr-1" /> Add</Button>
            </div>
            {newProject.contactPersons.map((person, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input value={person} onChange={(e) => updateContactPerson(index, e.target.value)} placeholder="Enter contact person name" className="flex-1" />
                {newProject.contactPersons.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeContactPerson(index)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label style={{ color: t1 }}>Contact Details *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addContactDetail}><Plus className="h-3 w-3 mr-1" /> Add</Button>
            </div>
            {newProject.contactDetails.map((detail, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input value={detail} onChange={(e) => updateContactDetail(index, e.target.value)} placeholder="Phone, mobile, etc." className="flex-1" />
                {newProject.contactDetails.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeContactDetail(index)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label style={{ color: t1 }}>Email Address *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addEmailAddress}><Plus className="h-3 w-3 mr-1" /> Add</Button>
            </div>
            {newProject.emailAddresses.map((email, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <Input type="email" value={email} onChange={(e) => updateEmailAddress(index, e.target.value)} placeholder="Enter email address" className="flex-1" />
                {newProject.emailAddresses.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeEmailAddress(index)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div>
            <Label style={{ color: t1 }}>Status</Label>
            <Select value={newProject.status} onValueChange={(v) => setNewProject({...newProject, status: v})}>
              <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Planning">Planning</SelectItem>
                <SelectItem value="Running">Running</SelectItem>
                <SelectItem value="Complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsCreateProjectModalOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} style={{ background: b2 }}>Create Project</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderEditProjectModal = () => (
    <Dialog open={isEditProjectModalOpen} onOpenChange={setIsEditProjectModalOpen}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Edit Project</DialogTitle>
          <DialogDescription style={{ color: t3 }}>Update the project details below</DialogDescription>
        </DialogHeader>
        {editingProject && (
          <div className="space-y-4 py-4">
            <div>
              <Label style={{ color: t1 }}>Project Title *</Label>
              <Input value={editingProject.title} onChange={(e) => setEditingProject({...editingProject, title: e.target.value})} />
            </div>
            <div>
              <Label style={{ color: t1 }}>Account Name *</Label>
              <Input value={editingProject.accountName} onChange={(e) => setEditingProject({...editingProject, accountName: e.target.value})} />
            </div>
            <div>
              <Label style={{ color: t1 }}>Company Address *</Label>
              <Textarea value={editingProject.companyAddress} onChange={(e) => setEditingProject({...editingProject, companyAddress: e.target.value})} />
            </div>
            <div>
              <Label style={{ color: t1 }}>Billing Statement Code</Label>
              <Input value={editingProject.billingStatementCode} onChange={(e) => setEditingProject({...editingProject, billingStatementCode: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: t1 }}>Start Date</Label>
                <Input type="date" value={editingProject.startDate || ''} onChange={(e) => setEditingProject({...editingProject, startDate: e.target.value})} />
              </div>
              <div>
                <Label style={{ color: t1 }}>End Date</Label>
                <Input type="date" value={editingProject.endDate || ''} onChange={(e) => setEditingProject({...editingProject, endDate: e.target.value})} />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Contact Person(s)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEditContactPerson}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {editingProject.contactPersons.map((person, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input value={person} onChange={(e) => updateEditContactPerson(index, e.target.value)} className="flex-1" />
                  {editingProject.contactPersons.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEditContactPerson(index)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Contact Details</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEditContactDetail}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {editingProject.contactDetails.map((detail, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input value={detail} onChange={(e) => updateEditContactDetail(index, e.target.value)} className="flex-1" />
                  {editingProject.contactDetails.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEditContactDetail(index)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Email Address(es)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEditEmailAddress}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {editingProject.emailAddresses.map((email, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input type="email" value={email} onChange={(e) => updateEditEmailAddress(index, e.target.value)} className="flex-1" />
                  {editingProject.emailAddresses.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEditEmailAddress(index)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Label style={{ color: t1 }}>Status</Label>
              <Select value={editingProject.status} onValueChange={(v) => setEditingProject({...editingProject, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Running">Running</SelectItem>
                  <SelectItem value="Complete">Complete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { setIsEditProjectModalOpen(false); setEditingProject(null); }}>Cancel</Button>
          <Button onClick={updateProject} style={{ background: b2 }}>Update Project</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // --- DEPARTMENT / MACHINE MODALS ---
  const renderDepartmentModals = () => (
    <>
      <Dialog open={isCreateDepartmentModalOpen} onOpenChange={setIsCreateDepartmentModalOpen}>
        <DialogContent className="nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Create New Department</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Add a new department to {selectedProject?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label style={{ color: t1 }}>Department Name *</Label>
              <Input value={newDepartment.name} onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })} placeholder="Enter department name" />
            </div>
            <div><Label style={{ color: t1 }}>Contact Person</Label>
              <Input value={newDepartment.contact_person} onChange={(e) => setNewDepartment({ ...newDepartment, contact_person: e.target.value })} placeholder="Contact person name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label style={{ color: t1 }}>Birthday</Label>
                <Input type="date" value={newDepartment.birthday} onChange={(e) => setNewDepartment({ ...newDepartment, birthday: e.target.value })} />
              </div>
              <div><Label style={{ color: t1 }}>Contact Number</Label>
                <Input value={newDepartment.contact_number} onChange={(e) => setNewDepartment({ ...newDepartment, contact_number: e.target.value })} placeholder="Phone / mobile" />
              </div>
            </div>
            <div><Label style={{ color: t1 }}>Email</Label>
              <Input type="email" value={newDepartment.email} onChange={(e) => setNewDepartment({ ...newDepartment, email: e.target.value })} placeholder="Email address" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateDepartmentModalOpen(false); setNewDepartment({ name: '', contact_person: '', birthday: '', contact_number: '', email: '' }); }}>Cancel</Button>
            <Button onClick={createDepartment} style={{ background: b2 }}>Create Department</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDepartmentModalOpen} onOpenChange={setIsEditDepartmentModalOpen}>
        <DialogContent className="nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Edit Department</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Update department information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label style={{ color: t1 }}>Department Name *</Label>
              <Input value={newDepartment.name} onChange={(e) => setNewDepartment({ ...newDepartment, name: e.target.value })} />
            </div>
            <div><Label style={{ color: t1 }}>Contact Person</Label>
              <Input value={newDepartment.contact_person} onChange={(e) => setNewDepartment({ ...newDepartment, contact_person: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label style={{ color: t1 }}>Birthday</Label>
                <Input type="date" value={newDepartment.birthday} onChange={(e) => setNewDepartment({ ...newDepartment, birthday: e.target.value })} />
              </div>
              <div><Label style={{ color: t1 }}>Contact Number</Label>
                <Input value={newDepartment.contact_number} onChange={(e) => setNewDepartment({ ...newDepartment, contact_number: e.target.value })} />
              </div>
            </div>
            <div><Label style={{ color: t1 }}>Email</Label>
              <Input type="email" value={newDepartment.email} onChange={(e) => setNewDepartment({ ...newDepartment, email: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditDepartmentModalOpen(false); setEditingDepartment(null); setNewDepartment({ name: '', contact_person: '', birthday: '', contact_number: '', email: '' }); }}>Cancel</Button>
            <Button onClick={updateDepartment} style={{ background: b2 }}>Update Department</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateMachineModalOpen} onOpenChange={setIsCreateMachineModalOpen}>
        <DialogContent className="nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Add New Machine</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Add machine to {selectedDepartmentForMachine?.name} department</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label style={{ color: t1 }}>Machine Model *</Label>
              <Input value={newMachine.model} onChange={(e) => setNewMachine({ ...newMachine, model: e.target.value })} placeholder="Enter machine model" />
            </div>
            <div><Label style={{ color: t1 }}>Serial Number *</Label>
              <Input value={newMachine.serialNumber} onChange={(e) => setNewMachine({ ...newMachine, serialNumber: e.target.value })} placeholder="Enter serial number" />
            </div>
            <div><Label style={{ color: t1 }}>Date Installed *</Label>
              <Input type="date" value={newMachine.dateInstalled} onChange={(e) => setNewMachine({ ...newMachine, dateInstalled: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateMachineModalOpen(false); setSelectedDepartmentForMachine(null); setNewMachine({ model: '', serialNumber: '', dateInstalled: '' }); }}>Cancel</Button>
            <Button onClick={createMachine} className="bg-green-600 hover:bg-green-700">Add Machine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditMachineModalOpen} onOpenChange={setIsEditMachineModalOpen}>
        <DialogContent className="nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Edit Machine</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Update machine information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label style={{ color: t1 }}>Machine Model *</Label>
              <Input value={newMachine.model} onChange={(e) => setNewMachine({ ...newMachine, model: e.target.value })} />
            </div>
            <div><Label style={{ color: t1 }}>Serial Number *</Label>
              <Input value={newMachine.serialNumber} onChange={(e) => setNewMachine({ ...newMachine, serialNumber: e.target.value })} />
            </div>
            <div><Label style={{ color: t1 }}>Date Installed *</Label>
              <Input type="date" value={newMachine.dateInstalled} onChange={(e) => setNewMachine({ ...newMachine, dateInstalled: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditMachineModalOpen(false); setEditingMachine(null); setSelectedDepartmentForMachine(null); setNewMachine({ model: '', serialNumber: '', dateInstalled: '' }); }}>Cancel</Button>
            <Button onClick={updateMachine} className="bg-green-600 hover:bg-green-700">Update Machine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // --- METER READING / MHR / RR MODALS (structure preserved) ---
  const renderMeterReadingModals = () => (
    <>
      <Dialog open={isMeterReadingModalOpen} onOpenChange={setIsMeterReadingModalOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Meter Reading - {selectedMachine?.model}</DialogTitle>
            <DialogDescription style={{ color: t3 }}>S/N: {selectedMachine?.serialNumber} | Enter present meter readings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Button variant="outline" size="sm" onClick={() => setIsInitialReadingModalOpen(true)}><Settings className="h-4 w-4 mr-2" /> Set Initial Readings</Button>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ background: s1 }}>
                    <TableHead style={{ color: t1 }}>Type</TableHead>
                    <TableHead style={{ color: t1 }}>Previous</TableHead>
                    <TableHead style={{ color: t1 }}>Present</TableHead>
                    <TableHead style={{ color: t1 }}>Multiplier</TableHead>
                    <TableHead style={{ color: t1 }}>Consumption</TableHead>
                    <TableHead style={{ color: t1 }}>Spoilage %</TableHead>
                    <TableHead style={{ color: t1 }}>Spoilage</TableHead>
                    <TableHead style={{ color: t1 }}>Rate</TableHead>
                    <TableHead style={{ color: t1 }}>Amount (₱)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'].map(type => {
                    const calc = calculateMeterReadingValues(meterReading)[type] || { totalConsumption: 0, spoilage: 0, amount: 0 };
                    return (
                      <TableRow key={type}>
                        <TableCell style={{ color: t1, fontWeight: 600 }}>{type}</TableCell>
                        <TableCell>
                          <div style={{ color: t1, fontWeight: 600 }}>{meterReading[type]?.previousReading || 0}</div>
                          <div className="text-xs" style={{ color: t4 }}>{meterReading[type]?.previousDate ? new Date(meterReading[type].previousDate).toLocaleDateString() : '--/--/--'}</div>
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={meterReading[type]?.presentReading || ''}
                            onChange={(e) => setMeterReading(prev => ({ ...prev, [type]: { ...prev[type], presentReading: e.target.value } }))}
                            className="text-center font-semibold" />
                          <Input type="date" value={meterReading[type]?.presentDate || ''}
                            onChange={(e) => setMeterReading(prev => ({ ...prev, [type]: { ...prev[type], presentDate: e.target.value } }))}
                            className="mt-1 text-xs" />
                        </TableCell>
                        <TableCell><Input type="number" min="0" step="0.01" value={meterReading[type]?.multiplier || 1}
                          onChange={(e) => setMeterReading(prev => ({ ...prev, [type]: { ...prev[type], multiplier: e.target.value || 1 } }))} className="text-center" /></TableCell>
                        <TableCell style={{ color: 'rgb(37,99,235)', fontWeight: 600, textAlign: 'center' }}>{calc.totalConsumption?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell><Input type="number" min="0" max="100" value={meterReading[type]?.spoilageTolerance || 0}
                          onChange={(e) => setMeterReading(prev => ({ ...prev, [type]: { ...prev[type], spoilageTolerance: e.target.value } }))} className="text-center" /></TableCell>
                        <TableCell style={{ color: 'rgb(234,88,12)', fontWeight: 600, textAlign: 'center' }}>{calc.spoilage?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell><Input type="number" min="0" step="0.01" value={meterReading[type]?.rate || 0}
                          onChange={(e) => setMeterReading(prev => ({ ...prev, [type]: { ...prev[type], rate: e.target.value } }))} className="text-center" /></TableCell>
                        <TableCell style={{ color: 'rgb(22,163,74)', fontWeight: 700, textAlign: 'center' }}>₱{calc.amount?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMeterReadingModalOpen(false)}>Cancel</Button>
            <Button onClick={saveMeterReading} style={{ background: b2 }}><Save className="h-4 w-4 mr-2" /> Save Readings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isInitialReadingModalOpen} onOpenChange={setIsInitialReadingModalOpen}>
        <DialogContent className="nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Set Initial Readings</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Set baseline meter values for {selectedMachine?.model}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'].map(type => (
              <div key={type} className="grid grid-cols-3 gap-4 items-end">
                <Label style={{ color: t1 }}>{type}</Label>
                <Input type="number" value={initialReading[type].value}
                  onChange={(e) => setInitialReading(prev => ({ ...prev, [type]: { ...prev[type], value: parseFloat(e.target.value) || 0 } }))}
                  placeholder="Initial reading value" />
                <Input type="date" value={initialReading[type].date}
                  onChange={(e) => setInitialReading(prev => ({ ...prev, [type]: { ...prev[type], date: e.target.value } }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInitialReadingModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveInitialReading} style={{ background: b2 }}>Save Initial Readings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isMHRModalOpen} onOpenChange={setIsMHRModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Machine History Repair - {selectedMachineForMHR?.model}</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Record and track machine repair history</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label style={{ color: t1 }}>Diagnosed Damage / Issue</Label>
              <Textarea value={machineRepair.diagnosedDamage}
                onChange={(e) => setMachineRepair({ ...machineRepair, diagnosedDamage: e.target.value })}
                placeholder="Describe the diagnosed damage or issue" />
              <div className="mt-1 grid grid-cols-2 gap-4">
                <div><Label style={{ color: t3, fontSize: '0.75rem' }}>Date Diagnosed</Label>
                  <Input type="date" value={machineRepair.diagnosedDate}
                    onChange={(e) => setMachineRepair({ ...machineRepair, diagnosedDate: e.target.value })} /></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Damaged Parts</Label>
                <div className="flex items-center gap-4">
                  <div><Label style={{ color: t3, fontSize: '0.75rem' }}>Date Noted</Label>
                    <Input type="date" value={machineRepair.damagedPartsDate}
                      onChange={(e) => setMachineRepair({ ...machineRepair, damagedPartsDate: e.target.value })} className="text-sm" /></div>
                  <Button type="button" variant="outline" size="sm" onClick={addDamagedPart}><Plus className="h-3 w-3 mr-1" /> Add Part</Button>
                </div>
              </div>
              {machineRepair.damagedParts.map((part, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={part} onChange={(e) => updateDamagedPart(i, e.target.value)} placeholder="Damaged part name" className="flex-1" />
                  {machineRepair.damagedParts.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeDamagedPart(i)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Parts Requested</Label>
                <div className="flex items-center gap-4">
                  <div><Label style={{ color: t3, fontSize: '0.75rem' }}>Date Requested</Label>
                    <Input type="date" value={machineRepair.partsRequestedDate}
                      onChange={(e) => setMachineRepair({ ...machineRepair, partsRequestedDate: e.target.value })} className="text-sm" /></div>
                  <Button type="button" variant="outline" size="sm" onClick={addPartsRequested}><Plus className="h-3 w-3 mr-1" /> Add Part</Button>
                </div>
              </div>
              {machineRepair.partsRequested.map((part, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={part} onChange={(e) => updatePartsRequested(i, e.target.value)} placeholder="Requested part name" className="flex-1" />
                  {machineRepair.partsRequested.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removePartsRequested(i)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label style={{ color: t1 }}>Parts Received Date</Label>
                <Input type="date" value={machineRepair.partsReceivedDate}
                  onChange={(e) => setMachineRepair({ ...machineRepair, partsReceivedDate: e.target.value })} /></div>
              <div><Label style={{ color: t1 }}>Repair Completed Date</Label>
                <Input type="date" value={machineRepair.repairCompletedDate}
                  onChange={(e) => setMachineRepair({ ...machineRepair, repairCompletedDate: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsMHRModalOpen(false); setEditingRepair(null); }}>Cancel</Button>
            <Button onClick={saveMachineRepair} className="bg-indigo-600 hover:bg-indigo-700">Save Repair Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRRModalOpen} onOpenChange={setIsRRModalOpen}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Reading Records - {selectedMachineForRR?.model}</DialogTitle>
            <DialogDescription style={{ color: t3 }}>S/N: {selectedMachineForRR?.serialNumber} - Historical meter reading records</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportMeterReadingsToCSV}><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
              <Button variant="outline" size="sm" onClick={() => setIsBatchReadingModalOpen(true)}><ArrowUpDown className="h-4 w-4 mr-2" /> Batch Edit</Button>
            </div>
            <ScrollArea className="max-h-[400px]">
              <Table>
                <TableHeader><TableRow style={{ background: s1 }}>
                  <TableHead style={{ color: t1 }}>Date</TableHead>
                  <TableHead style={{ color: t1 }}>Type</TableHead>
                  <TableHead style={{ color: t1 }}>Previous</TableHead>
                  <TableHead style={{ color: t1 }}>Present</TableHead>
                  <TableHead style={{ color: t1 }}>Consumption</TableHead>
                  <TableHead style={{ color: t1 }}>Spoilage</TableHead>
                  <TableHead style={{ color: t1 }}>Rate</TableHead>
                  <TableHead style={{ color: t1 }}>Amount</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(() => {
                    const readings = selectedMachineForRR?.meterReadings || [];
                    if (readings.length === 0) return <TableRow><TableCell colSpan={8} style={{ textAlign: 'center', color: t4, padding: '2rem' }}>No reading records yet</TableCell></TableRow>;
                    return readings.map(r => (
                      <TableRow key={r.id}>
                        <TableCell style={{ color: t1 }}>{r.presentDate ? new Date(r.presentDate).toLocaleDateString() : '--/--/--'}</TableCell>
                        <TableCell style={{ color: t1, fontWeight: 500 }}>{r.type}</TableCell>
                        <TableCell style={{ color: t3 }}>{r.previousReading || 0}</TableCell>
                        <TableCell style={{ color: t1, fontWeight: 600 }}>{r.presentReading || 0}</TableCell>
                        <TableCell style={{ color: 'rgb(37,99,235)', fontWeight: 600 }}>{r.totalConsumption?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell style={{ color: 'rgb(234,88,12)' }}>{r.spoilage?.toFixed(2) || '0.00'} ({r.spoilageTolerance}%)</TableCell>
                        <TableCell style={{ color: t3 }}>₱{r.rate || 0}</TableCell>
                        <TableCell style={{ color: 'rgb(22,163,74)', fontWeight: 700 }}>₱{r.amount?.toFixed(2) || '0.00'}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRRModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // ============================================================
  // MAIN UI: PROJECT LIST VIEW OR PROJECT DETAIL VIEW
  // ============================================================

  const renderProjectDetailView = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => setSelectedProject(null)} size="sm">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to Projects
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold truncate" style={{ color: t1 }}>{selectedProject.title || selectedProject.name}</h2>
          <p className="mt-1 text-sm truncate" style={{ color: t3 }}>{selectedProject.description || selectedProject.accountName || 'No description'}</p>
        </div>
        <div className="text-xs whitespace-nowrap" style={{ color: t4 }}>{currentDateTime}</div>
      </div>

      <div className="border-b" style={{ borderColor: contentBorder }}>
        <div className="flex space-x-1 overflow-x-auto">
          {[
            { key: 'overview', label: 'Project Overview' },
            { key: 'meter', label: 'Meter Reading' },
            { key: 'billing', label: 'Billing Statement' },
            { key: 'si', label: 'Sales Invoice' },
            { key: 'collection', label: 'Collection' },
            { key: 'settings', label: 'Settings' }
          ].map(tab => (
            <button key={tab.key} onClick={() => setProjectDetailTab(tab.key)}
              className={cn('px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap border-b-2',
                projectDetailTab === tab.key ? 'border-indigo-600' : 'border-transparent hover:border-gray-300')}
              style={{ color: projectDetailTab === tab.key ? 'rgb(99,102,241)' : t3 }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
        {/* ===== TAB 1: PROJECT OVERVIEW ===== */}
        {projectDetailTab === 'overview' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle style={{ color: t1 }}>Project Overview</CardTitle>
              <CardDescription style={{ color: t3 }}>Project details and information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div onClick={() => setIsDepartmentsModalOpen(true)}
                  className="cursor-pointer hover:shadow-lg transition-all rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(59,130,246,0.04))', border: '2px solid rgba(59,130,246,0.3)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: 'rgb(37,99,235)' }}>Departments</h3>
                    <Building2 className="h-8 w-8" style={{ color: 'rgb(59,130,246)' }} />
                  </div>
                  <p className="text-3xl font-bold" style={{ color: 'rgb(30,64,175)' }}>
                    <AnimatedNumber value={String(departments?.length || 0)} delay={520} duration={1300} />
                  </p>
                  <p className="text-xs mt-2" style={{ color: 'rgba(59,130,246,0.85)' }}>Click to view list</p>
                </div>
                <div onClick={() => setIsMachinesModalOpen(true)}
                  className="cursor-pointer hover:shadow-lg transition-all rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.14), rgba(168,85,247,0.04))', border: '2px solid rgba(168,85,247,0.3)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: 'rgb(126,34,206)' }}>Machines</h3>
                    <Printer className="h-8 w-8" style={{ color: 'rgb(168,85,247)' }} />
                  </div>
                  <p className="text-3xl font-bold" style={{ color: 'rgb(109,40,217)' }}>
                    <AnimatedNumber
                      value={String(departments?.reduce((total, dept) => total + (dept.machines?.length || 0), 0) || 0)}
                      delay={640}
                      duration={1350}
                    />
                  </p>
                  <p className="text-xs mt-2" style={{ color: 'rgba(168,85,247,0.85)' }}>Click to view list</p>
                </div>
                <div onClick={() => setIsEarningsModalOpen(true)}
                  className="cursor-pointer hover:shadow-lg transition-all rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(34,197,94,0.04))', border: '2px solid rgba(34,197,94,0.3)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: 'rgb(22,101,52)' }}>Total Earnings</h3>
                    <DollarSign className="h-8 w-8" style={{ color: 'rgb(34,197,94)' }} />
                  </div>
                  <p className="text-3xl font-bold" style={{ color: 'rgb(21,128,61)' }}>
                    ₱<AnimatedNumber
                      value={collections?.reduce((total, c) => total + (c.totalAmount || 0), 0).toFixed(2) || '0.00'}
                      delay={760}
                      duration={1400}
                    />
                  </p>
                  <p className="text-xs mt-2" style={{ color: 'rgba(34,197,94,0.85)' }}>Click to view breakdown</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium" style={{ color: t4 }}>Project Title</p>
                    <p className="text-lg font-semibold mt-1" style={{ color: t1 }}>{selectedProject.title || selectedProject.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: t4 }}>Status</p>
                    <span className={cn('inline-flex px-3 py-1 text-sm font-medium rounded-full mt-1',
                      selectedProject.status === 'Complete' ? 'bg-blue-100 text-blue-800' :
                      selectedProject.status === 'Running' ? 'bg-green-100 text-green-800' :
                      selectedProject.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800')}>
                      {selectedProject.status}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: t4 }}>Account Name</p>
                  <p className="text-lg mt-1" style={{ color: t1 }}>{selectedProject.accountName || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: t4 }}>Company Address</p>
                  <p className="mt-1" style={{ color: t1 }}>{selectedProject.companyAddress || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: t4 }}>Billing Statement Code</p>
                  <p className="text-lg font-semibold mt-1" style={{ color: 'rgb(99,102,241)' }}>{selectedProject.billingStatementCode || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: t4 }}>Contact Person(s)</p>
                  {selectedProject.contactPersons && selectedProject.contactPersons.filter(p => p).length > 0 ? (
                    <ul className="space-y-1">
                      {selectedProject.contactPersons.filter(p => p).map((person, idx) => (
                        <li key={idx} className="pl-4 border-l-2 border-indigo-200" style={{ color: t1 }}>{person}</li>
                      ))}
                    </ul>
                  ) : <p style={{ color: t4 }}>No contact persons</p>}
                </div>
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: t4 }}>Contact Details</p>
                  {selectedProject.contactDetails && selectedProject.contactDetails.filter(d => d).length > 0 ? (
                    <ul className="space-y-1">
                      {selectedProject.contactDetails.filter(d => d).map((detail, idx) => (
                        <li key={idx} className="pl-4 border-l-2 border-green-200" style={{ color: t1 }}>{detail}</li>
                      ))}
                    </ul>
                  ) : <p style={{ color: t4 }}>No contact details</p>}
                </div>
                <div>
                  <p className="text-sm font-medium mb-2" style={{ color: t4 }}>Email Address(es)</p>
                  {selectedProject.emailAddresses && selectedProject.emailAddresses.filter(e => e).length > 0 ? (
                    <ul className="space-y-1">
                      {selectedProject.emailAddresses.filter(e => e).map((email, idx) => (
                        <li key={idx} className="pl-4 border-l-2 border-blue-200">
                          <a href={`mailto:${email}`} className="text-indigo-600 hover:text-indigo-800">{email}</a>
                        </li>
                      ))}
                    </ul>
                  ) : <p style={{ color: t4 }}>No email addresses</p>}
                </div>
                <div className="grid grid-cols-2 gap-6 pt-4 border-t" style={{ borderColor: contentBorder }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: t4 }}>Start Date</p>
                    <p className="text-lg mt-1" style={{ color: t1 }}>{selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: t4 }}>End Date</p>
                    <p className="text-lg mt-1" style={{ color: t1 }}>{selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'Not set'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: t4 }}>Created</p>
                  <p className="mt-1" style={{ color: t1 }}>{selectedProject.createdAt ? new Date(selectedProject.createdAt).toLocaleString() : 'N/A'}</p>
                </div>

                {projectTasks && projectTasks.length > 0 && (
                  <div className="mt-8 pt-6 border-t" style={{ borderColor: contentBorder }}>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: t1 }}>Task Summary</h3>
                    <div className="space-y-4">
                      {projectTasks.map((task, taskIndex) => {
                        const taskDates = getTaskDateRange(task);
                        const attendedDates = taskAttendedDates[task.id] || [];
                        const totalDays = taskDates.length;
                        const attendedDays = attendedDates.length;
                        const unattendedDays = totalDays - attendedDays;
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const endDate = new Date(task.end_date); endDate.setHours(0, 0, 0, 0);
                        const isPastDue = today > endDate;
                        const daysPastDue = isPastDue ? Math.ceil((today - endDate) / (1000 * 60 * 60 * 24)) : 0;
                        const completion = calculateTaskCompletion(task);
                        const status = calculateTaskStatus(task);
                        return (
                          <div key={task.id} className="rounded-lg p-4" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate" style={{ color: t1 }}>{task.task_name}</h4>
                                <p className="text-sm" style={{ color: t3 }}>Assigned to: {task.assigned_resources}</p>
                                <p className="text-xs" style={{ color: t4 }}>
                                  {new Date(task.start_date).toLocaleDateString()} - {new Date(task.end_date).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center gap-4">
                                {isPastDue && (
                                  <div className="text-right">
                                    <Badge className="bg-red-100 text-red-800">Past Due</Badge>
                                    <p className="text-xs text-red-600 mt-1">{daysPastDue} days past due</p>
                                  </div>
                                )}
                                <Badge className={cn(
                                  status === 'Completed' ? 'bg-green-100 text-green-800' :
                                  status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800')}>
                                  {status}
                                </Badge>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="text-center p-3 rounded-lg bg-gray-50">
                                <div className="text-2xl font-bold" style={{ color: t1 }}>
                                  <AnimatedNumber value={String(totalDays)} delay={900 + taskIndex * 90} duration={1100} />
                                </div>
                                <div className="text-xs" style={{ color: t3 }}>Total Days</div>
                              </div>
                              <div className="text-center p-3 rounded-lg bg-green-50">
                                <div className="text-2xl font-bold text-green-700">
                                  <AnimatedNumber value={String(attendedDays)} delay={980 + taskIndex * 90} duration={1100} />
                                </div>
                                <div className="text-xs text-green-600">Attended</div>
                              </div>
                              <div className="text-center p-3 rounded-lg bg-red-50">
                                <div className="text-2xl font-bold text-red-700">
                                  <AnimatedNumber value={String(unattendedDays)} delay={1060 + taskIndex * 90} duration={1100} />
                                </div>
                                <div className="text-xs text-red-600">Unattended</div>
                              </div>
                              <div className="text-center p-3 rounded-lg bg-blue-50">
                                <div className={cn('text-2xl font-bold',
                                  completion >= 75 ? 'text-green-700' : completion >= 50 ? 'text-blue-700' : completion >= 25 ? 'text-orange-700' : 'text-gray-700')}>
                                  <AnimatedNumber value={`${completion}%`} delay={1140 + taskIndex * 90} duration={1150} />
                                </div>
                                <div className="text-xs" style={{ color: t3 }}>Complete</div>
                              </div>
                            </div>
                            <div className="mt-3">
                              <div className="flex justify-between text-xs mb-1" style={{ color: t3 }}>
                                <span>Progress</span>
                                <span>{attendedDays} of {totalDays} days</span>
                              </div>
                              <div className="w-full rounded-full h-2" style={{ background: s1 }}>
                                <div className={cn('h-2 rounded-full',
                                  completion >= 75 ? 'bg-green-600' : completion >= 50 ? 'bg-blue-600' : completion >= 25 ? 'bg-orange-600' : 'bg-gray-600')}
                                  style={{ width: `${completion}%` }} />
                              </div>
                            </div>
                            <div className="mt-3 flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setSelectedTaskForCalendar(task); }}>
                                <CalendarIcon className="h-4 w-4 mr-1" /> Calendar / Attendance
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => saveTaskAttendance(task.id)}>
                                <Save className="h-4 w-4 mr-1" /> Save Attendance
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteProjectTask(selectedProject.id, task.id)}>
                                <Trash2 className="h-4 w-4 mr-1" /> Delete Task
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {projectTasks && projectTasks.length === 0 && (
                  <div className="mt-8 pt-6 border-t text-center py-8" style={{ borderColor: contentBorder }}>
                    <Calendar className="h-12 w-12 mx-auto mb-3" style={{ color: t4, opacity: 0.5 }} />
                    <p className="text-sm" style={{ color: t4 }}>No tasks added yet. Go to Project Settings to add tasks.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== TAB 2: METER READING ===== */}
        {projectDetailTab === 'meter' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle className="flex flex-wrap justify-between items-center gap-2">
                <span style={{ color: t1 }}>Meter Reading</span>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={exportDepartmentDataCSV} className="flex items-center gap-2"><Download className="h-4 w-4" /> Export CSV</Button>
                  <Button variant="outline" onClick={() => setIsFilterExportModalOpen(true)} className="flex items-center gap-2"><Filter className="h-4 w-4" /> Filter Export</Button>
                  <Button onClick={() => setIsCreateDepartmentModalOpen(true)} style={{ background: b2 }}><Plus className="h-4 w-4 mr-1" /> New Department</Button>
                </div>
              </CardTitle>
              <CardDescription style={{ color: t3 }}>Track and manage meter readings by department</CardDescription>
            </CardHeader>
            <CardContent>
              {departments.length === 0 ? (
                <div className="text-center py-12">
                  <Building className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                  <h3 className="text-lg font-medium mb-2" style={{ color: t1 }}>No Departments Yet</h3>
                  <p className="mb-4" style={{ color: t3 }}>Create your first department to start managing meter readings.</p>
                  <Button onClick={() => setIsCreateDepartmentModalOpen(true)} style={{ background: b2 }}><Plus className="h-4 w-4 mr-1" /> Create First Department</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {(departments || []).map(dept => (
                    <div key={dept.id} className="rounded-lg p-6" style={{ background: s1, border: `2px solid ${contentBorder}` }}>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold" style={{ color: t1 }}>{dept.name || 'Unnamed Department'}</h3>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => editDepartment(dept)} className="text-blue-600"><Edit className="h-3 w-3 mr-1" /> Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => deleteDepartment(dept)} className="text-red-600"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
                          <Button size="sm" onClick={() => { setSelectedDepartmentForMachine(dept); setIsCreateMachineModalOpen(true); }} className="bg-green-600 hover:bg-green-700"><Plus className="h-3 w-3 mr-1" /> Machine</Button>
                        </div>
                      </div>
                      {(!dept.machines || dept.machines.length === 0) ? (
                        <div className="text-center py-8 rounded-lg bg-white border-2 border-dashed" style={{ borderColor: contentBorder }}>
                          <Cog className="h-10 w-10 mx-auto mb-2" style={{ color: t4, opacity: 0.5 }} />
                          <p className="text-sm" style={{ color: t3 }}>No machines added yet</p>
                          <p className="text-xs" style={{ color: t4 }}>Click "+ Machine" to add the first machine</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {dept.machines.map(machine => {
                            const repairStatus = getMachineRepairStatus(machine);
                            const mhrButtonColor = repairStatus === 'completed' ? 'bg-green-500 hover:bg-green-600' : repairStatus === 'parts-requested' ? 'bg-yellow-500 hover:bg-yellow-600' : repairStatus === 'diagnosed' ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-500 hover:bg-gray-600';
                            return (
                              <div key={machine.id} className="rounded-lg p-4 hover:shadow-md transition-all" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="p-2 rounded-lg" style={{ background: 'rgba(99,102,241,0.12)' }}>
                                      <Cog className="h-5 w-5" style={{ color: 'rgb(99,102,241)' }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-medium truncate" style={{ color: t1 }}>{machine.model || 'Unknown Model'}</h4>
                                      <p className="text-sm truncate" style={{ color: t4 }}>S/N: {machine.serialNumber || 'N/A'}</p>
                                      <p className="text-xs" style={{ color: t4 }}>Installed: {machine.dateInstalled ? new Date(machine.dateInstalled).toLocaleDateString() : 'N/A'}</p>
                                      <p className="text-xs font-semibold mt-1 text-green-600">
                                        Earnings: ₱{(machine.meterReadings?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0).toFixed(2)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <div className="flex gap-1 justify-end">
                                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); editMachine(machine, dept); }} className="text-blue-600 text-xs px-2 py-1 h-7"><Edit className="h-3 w-3" /></Button>
                                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); deleteMachine(machine, dept); }} className="text-red-600 text-xs px-2 py-1 h-7"><Trash2 className="h-3 w-3" /></Button>
                                    </div>
                                    <div className="flex gap-1 justify-end">
                                      <Button size="sm" className={`text-white text-xs px-2 py-1 h-7 ${mhrButtonColor}`}
                                        onClick={(e) => { e.stopPropagation(); setSelectedMachineForMHR(machine); setMachineRepair({ diagnosedDamage: '', diagnosedDate: '', damagedParts: [''], damagedPartsDate: '', partsRequested: [''], partsRequestedDate: '', partsReceivedDate: '', repairCompletedDate: '' }); setIsMHRModalOpen(true); }}>MHR</Button>
                                      <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 h-7"
                                        onClick={(e) => { e.stopPropagation(); setSelectedMachineForRR(machine); setIsRRModalOpen(true); }}>RR</Button>
                                    </div>
                                  </div>
                                </div>
                                <div className="cursor-pointer" onClick={async () => {
                                  const currentMachineId = machine.id;
                                  try {
                                    const response = await axiosInstance.get(`/projects/${selectedProject.id}/departments`);
                                    const freshDepartments = Array.isArray(response.data) ? response.data : [];
                                    let freshMachine = machine;
                                    for (const d of freshDepartments) {
                                      const fm = d.machines?.find(m => m.id === currentMachineId);
                                      if (fm) { freshMachine = fm; break; }
                                    }
                                    const meterTypes = ['Mono Small', 'Mono Large', 'Colored Small', 'Colored Large'];
                                    const initState = {};
                                    meterTypes.forEach(type => {
                                      const typeReadings = freshMachine.meterReadings?.filter(r => r.type === type) || [];
                                      if (typeReadings.length > 0) {
                                        const sorted = [...typeReadings].sort((a, b) => new Date(b.presentDate).getTime() - new Date(a.presentDate).getTime());
                                        const latest = sorted[0];
                                        initState[type] = { previousReading: latest.presentReading, previousDate: latest.presentDate, presentReading: '', presentDate: '', multiplier: latest.multiplier || 1, spoilageTolerance: latest.spoilageTolerance || 0, rate: latest.rate || 0 };
                                      } else if (freshMachine.initialReadings && freshMachine.initialReadings[type] && freshMachine.initialReadings[type].value) {
                                        initState[type] = { previousReading: parseFloat(freshMachine.initialReadings[type].value), previousDate: freshMachine.initialReadings[type].date, presentReading: '', presentDate: '', multiplier: 1, spoilageTolerance: 0, rate: 0 };
                                      } else {
                                        initState[type] = { previousReading: 0, previousDate: '', presentReading: '', presentDate: '', multiplier: 1, spoilageTolerance: 0, rate: 0 };
                                      }
                                    });
                                    setSelectedMachine(freshMachine);
                                    setMeterReading(initState);
                                    setIsMeterReadingModalOpen(true);
                                  } catch (err) {
                                    console.warn('[MPSProjects] Error loading fresh readings:', err?.message || err);
                                    toast.error('Failed to load latest readings');
                                  }
                                }}>
                                  <div className="text-center py-2 rounded border-2 border-dashed hover:opacity-80 transition-opacity" style={{ borderColor: contentBorder, background: s1 }}>
                                    <p className="text-sm" style={{ color: t3 }}>Click to add meter reading</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== TAB 3: BILLING STATEMENT ===== */}
        {projectDetailTab === 'billing' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle style={{ color: t1 }}>Billing Statement</CardTitle>
              <CardDescription style={{ color: t3 }}>Generate bills for meter readings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 rounded-lg" style={{ background: s1 }}>
                <Label className="block text-sm font-medium mb-2" style={{ color: t3 }}>Billing Statement Code</Label>
                <div className="text-lg font-bold" style={{ color: 'rgb(99,102,241)' }}>
                  {selectedProject?.billingStatementCode || 'Not Set - Please set in Project Settings'}
                </div>
              </div>
              {departments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                  <h3 className="text-lg font-medium mb-2" style={{ color: t1 }}>No Departments Available</h3>
                  <p style={{ color: t3 }}>Go to Meter Reading tab to add departments and machines first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {departments.map(dept => (
                    <div key={dept.id} className="rounded-lg p-6" style={{ background: s1, border: `2px solid ${contentBorder}` }}>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold" style={{ color: t1 }}>{dept.name}</h3>
                      </div>
                      {(!dept.machines || dept.machines.length === 0) ? (
                        <div className="text-center py-8 rounded-lg border-2 border-dashed bg-white" style={{ borderColor: contentBorder }}>
                          <Cog className="h-10 w-10 mx-auto mb-2" style={{ color: t4, opacity: 0.5 }} />
                          <p className="text-sm" style={{ color: t3 }}>No machines in this department</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {dept.machines.map(machine => {
                            const allReadings = machine.meterReadings || [];
                            const sortedReadings = [...allReadings].sort((a, b) => new Date(b.presentDate).getTime() - new Date(a.presentDate).getTime());
                            const latestReading = sortedReadings[0];
                            const colorClasses = getBillingMachineColorClass(machine);
                            const installedDate = machine.dateInstalled ? new Date(machine.dateInstalled).toLocaleDateString() : 'N/A';
                            const latestDate = latestReading?.presentDate ? new Date(latestReading.presentDate).toLocaleDateString() : null;
                            return (
                              <div key={machine.id} onClick={async () => {
                                setAcknowledgedBillingMachines(prev => new Set(prev).add(machine.id));
                                try {
                                  const resp = await axiosInstance.get(`/machines/${machine.id}/meter-readings`);
                                  const readings = Array.isArray(resp.data) ? resp.data : [];
                                  const grouped = readings.reduce((acc, r) => { if (!acc[r.presentDate]) acc[r.presentDate] = []; acc[r.presentDate].push(r); return acc; }, {});
                                  const records = Object.keys(grouped).map(date => ({
                                    date, readingsCount: grouped[date].length,
                                    readingTypes: grouped[date].map(r => r.type).filter(Boolean),
                                    totalAmount: grouped[date].reduce((sum, r) => sum + (r.amount || 0), 0),
                                    readings: grouped[date]
                                  })).sort((a, b) => new Date(b.date) - new Date(a.date));
                                  setBillingRecordsData(records);
                                  setSelectedMachineForBillingRecords({ ...machine, departmentName: dept.name });
                                  setIsBillingRecordsModalOpen(true);
                                } catch (err) {
                                  console.warn('[MPSProjects] Billing records fetch failed:', err?.message || err);
                                  toast.error('Failed to fetch meter readings');
                                }
                              }} className={`${colorClasses.bg} border-2 ${colorClasses.border} rounded-lg p-4 hover:shadow-md transition-all cursor-pointer`}>
                                <div className="flex items-center gap-3 mb-2">
                                  <div className={`${colorClasses.icon} p-2 rounded-lg`}>
                                    <Printer className={`h-4 w-4 ${colorClasses.iconText}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className={`font-medium truncate ${colorClasses.text}`}>{machine.model}</h4>
                                    <p className="text-xs truncate" style={{ color: t3 }}>S/N: {machine.serialNumber}</p>
                                  </div>
                                </div>
                                <div className="text-xs space-y-1" style={{ color: t3 }}>
                                  <p>Installed: {installedDate}</p>
                                  {latestDate && <p className="font-medium">Latest: {latestDate}</p>}
                                </div>
                                <div className={`mt-2 pt-2 border-t ${colorClasses.border}`}>
                                  <span className={`text-xs font-semibold ${colorClasses.text}`}>Click to view records</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4" style={{ color: t1 }}>Billing History</h3>
                <div className="mb-4 p-4 rounded-lg" style={{ background: s1 }}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><Label style={{ color: t3 }}>Start Date</Label>
                      <Input type="date" value={billingHistoryFilter.startDate}
                        onChange={(e) => { setBillingHistoryFilter({ ...billingHistoryFilter, startDate: e.target.value }); setBillingHistoryPage(1); }} /></div>
                    <div><Label style={{ color: t3 }}>End Date</Label>
                      <Input type="date" value={billingHistoryFilter.endDate}
                        onChange={(e) => { setBillingHistoryFilter({ ...billingHistoryFilter, endDate: e.target.value }); setBillingHistoryPage(1); }} /></div>
                    <div className="md:col-span-2"><Label style={{ color: t3 }}>Search</Label>
                      <Input type="text" placeholder="BS #, Department, Machine, Serial #..."
                        value={billingHistoryFilter.searchQuery}
                        onChange={(e) => setBillingHistoryFilter({ ...billingHistoryFilter, searchQuery: e.target.value })} /></div>
                  </div>
                </div>
                {billingExports.length === 0 ? (
                  <div className="text-center py-12">
                    <FileDown className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                    <p style={{ color: t3 }}>No billing exports yet. Billing statements are generated from approved meter reading records.</p>
                  </div>
                ) : (() => {
                  const filteredExports = billingExports.filter(exp => {
                    if (billingHistoryFilter.startDate) {
                      const expDate = exp.createdAt ? new Date(exp.createdAt) : new Date(0);
                      if (expDate < new Date(billingHistoryFilter.startDate)) return false;
                    }
                    if (billingHistoryFilter.endDate) {
                      const expDate = exp.createdAt ? new Date(exp.createdAt) : new Date(0);
                      const endDay = new Date(billingHistoryFilter.endDate); endDay.setHours(23, 59, 59, 999);
                      if (expDate > endDay) return false;
                    }
                    if (billingHistoryFilter.searchQuery) {
                      const q = billingHistoryFilter.searchQuery.toLowerCase();
                      const haystack = `${exp.billingStatementNumber || ''} ${exp.departmentName || ''} ${exp.machineModel || ''} ${exp.serialNumber || ''}`.toLowerCase();
                      if (!haystack.includes(q)) return false;
                    }
                    return true;
                  });
                  const totalPages = Math.ceil(filteredExports.length / ITEMS_PER_PAGE);
                  const startIdx = (billingHistoryPage - 1) * ITEMS_PER_PAGE;
                  const pageRecords = filteredExports.slice(startIdx, startIdx + ITEMS_PER_PAGE);
                  return (
                    <div>
                      <ScrollArea className="max-h-[400px] rounded-lg border" style={{ borderColor: contentBorder }}>
                        <Table>
                          <TableHeader><TableRow style={{ background: s1 }}>
                            <TableHead style={{ color: t1 }}>BS #</TableHead>
                            <TableHead style={{ color: t1 }}>Date</TableHead>
                            <TableHead style={{ color: t1 }}>Dept / Machine</TableHead>
                            <TableHead style={{ color: t1 }}>Serial #</TableHead>
                            <TableHead style={{ color: t1 }} className="text-right">Total Amount</TableHead>
                            <TableHead style={{ color: t1 }}>Status</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {pageRecords.map((exp, idx) => (
                              <TableRow key={exp.id || idx}>
                                <TableCell style={{ color: t1, fontWeight: 600 }}>{exp.billingStatementNumber || `BS-${exp.id}`}</TableCell>
                                <TableCell style={{ color: t3 }}>{exp.createdAt ? new Date(exp.createdAt).toLocaleDateString() : '--/--/--'}</TableCell>
                                <TableCell style={{ color: t1 }}>{exp.departmentName || exp.machineModel || 'N/A'}</TableCell>
                                <TableCell style={{ color: t3 }}>{exp.serialNumber || '--'}</TableCell>
                                <TableCell style={{ color: t1, fontWeight: 700, textAlign: 'right' }}>₱{(exp.totalAmount || 0).toFixed(2)}</TableCell>
                                <TableCell><Badge className={exp.isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {exp.isApproved ? 'Approved' : 'Pending'}
                                </Badge></TableCell>
                              </TableRow>
                            ))}
                            {pageRecords.length === 0 && (
                              <TableRow><TableCell colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: t4 }}>No billing records match the filters.</TableCell></TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                      {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                          <p className="text-sm" style={{ color: t4 }}>Showing {startIdx + 1}-{Math.min(startIdx + ITEMS_PER_PAGE, filteredExports.length)} of {filteredExports.length}</p>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={billingHistoryPage === 1} onClick={() => setBillingHistoryPage(p => Math.max(1, p - 1))}>Prev</Button>
                            <Button variant="outline" size="sm" disabled={billingHistoryPage === totalPages} onClick={() => setBillingHistoryPage(p => Math.min(totalPages, p + 1))}>Next</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== TAB 4: SALES INVOICE ===== */}
        {projectDetailTab === 'si' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center flex-wrap gap-2">
                <span style={{ color: t1 }}>Sales Invoice (SI)</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setIsConsolidatedSIModalOpen(true)}><FileText className="h-4 w-4 mr-2" /> Consolidated SI</Button>
                </div>
              </CardTitle>
              <CardDescription style={{ color: t3 }}>Track and manage sales invoices for the project</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 rounded-lg" style={{ background: s1 }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label style={{ color: t3 }}>Start Date</Label>
                    <Input type="date" value={siHistoryFilter.startDate}
                      onChange={(e) => { setSiHistoryFilter({ ...siHistoryFilter, startDate: e.target.value }); setSiHistoryPage(1); }} /></div>
                  <div><Label style={{ color: t3 }}>End Date</Label>
                    <Input type="date" value={siHistoryFilter.endDate}
                      onChange={(e) => { setSiHistoryFilter({ ...siHistoryFilter, endDate: e.target.value }); setSiHistoryPage(1); }} /></div>
                  <div><Label style={{ color: t3 }}>Search</Label>
                    <Input type="text" placeholder="SI #, Amount..." value={siHistoryFilter.searchQuery}
                      onChange={(e) => setSiHistoryFilter({ ...siHistoryFilter, searchQuery: e.target.value })} /></div>
                </div>
              </div>
              {siExports.length === 0 ? (
                <div className="text-center py-16">
                  <FileText className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                  <h3 className="text-lg font-medium mb-2" style={{ color: t1 }}>No Sales Invoices Yet</h3>
                  <p style={{ color: t3 }}>Sales invoices are generated after billing statements are approved.</p>
                </div>
              ) : (() => {
                const filtered = siExports.filter(si => {
                  if (siHistoryFilter.startDate) {
                    const d = si.createdAt ? new Date(si.createdAt) : new Date(0);
                    if (d < new Date(siHistoryFilter.startDate)) return false;
                  }
                  if (siHistoryFilter.endDate) {
                    const d = si.createdAt ? new Date(si.createdAt) : new Date(0);
                    const endDay = new Date(siHistoryFilter.endDate); endDay.setHours(23, 59, 59, 999);
                    if (d > endDay) return false;
                  }
                  if (siHistoryFilter.searchQuery) {
                    const q = siHistoryFilter.searchQuery.toLowerCase();
                    if (!`${si.salesInvoiceNumber || ''} ${si.totalAmount || ''}`.toLowerCase().includes(q)) return false;
                  }
                  return true;
                });
                const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                const startIdx = (siHistoryPage - 1) * ITEMS_PER_PAGE;
                const pageRecords = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE);
                return (
                  <div>
                    <ScrollArea className="max-h-[450px] rounded-lg border" style={{ borderColor: contentBorder }}>
                      <Table>
                        <TableHeader><TableRow style={{ background: s1 }}>
                          <TableHead style={{ color: t1 }}>SI #</TableHead>
                          <TableHead style={{ color: t1 }}>Date Issued</TableHead>
                          <TableHead style={{ color: t1 }}>BS Reference</TableHead>
                          <TableHead style={{ color: t1 }} className="text-right">Amount (₱)</TableHead>
                          <TableHead style={{ color: t1 }}>Status</TableHead>
                          <TableHead style={{ color: t1 }} className="text-right">Actions</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {pageRecords.map((si, idx) => (
                            <TableRow key={si.id || idx}>
                              <TableCell style={{ color: t1, fontWeight: 600 }}>{si.salesInvoiceNumber || `SI-${si.id}`}</TableCell>
                              <TableCell style={{ color: t3 }}>{si.createdAt ? new Date(si.createdAt).toLocaleDateString() : '--/--/--'}</TableCell>
                              <TableCell style={{ color: t3 }}>{si.billingExportRef || si.billingStatementNumber || '--'}</TableCell>
                              <TableCell style={{ color: t1, fontWeight: 700, textAlign: 'right' }}>₱{(si.totalAmount || 0).toFixed(2)}</TableCell>
                              <TableCell><Badge className={si.isPaid ? 'bg-green-100 text-green-800' : si.isSent ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
                                {si.isPaid ? 'Paid' : si.isSent ? 'Sent' : 'Draft'}
                              </Badge></TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedSIData(si); setIsSIPreviewOpen(true); }}><FileText className="h-3 w-3 mr-1" /> Preview</Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {pageRecords.length === 0 && (
                            <TableRow><TableCell colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: t4 }}>No SI records match the filters.</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                    {totalPages > 1 && (
                      <div className="flex justify-between items-center mt-4">
                        <p className="text-sm" style={{ color: t4 }}>Showing {startIdx + 1}-{Math.min(startIdx + ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={siHistoryPage === 1} onClick={() => setSiHistoryPage(p => Math.max(1, p - 1))}>Prev</Button>
                          <Button variant="outline" size="sm" disabled={siHistoryPage === totalPages} onClick={() => setSiHistoryPage(p => Math.min(totalPages, p + 1))}>Next</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* ===== TAB 5: COLLECTION ===== */}
        {projectDetailTab === 'collection' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle style={{ color: t1 }}>Collection</CardTitle>
              <CardDescription style={{ color: t3 }}>Track payments and collection status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 rounded-lg" style={{ background: s1 }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><Label style={{ color: t3 }}>Start Date</Label>
                    <Input type="date" value={collectionHistoryFilter.startDate}
                      onChange={(e) => { setCollectionHistoryFilter({ ...collectionHistoryFilter, startDate: e.target.value }); setCollectionHistoryPage(1); }} /></div>
                  <div><Label style={{ color: t3 }}>End Date</Label>
                    <Input type="date" value={collectionHistoryFilter.endDate}
                      onChange={(e) => { setCollectionHistoryFilter({ ...collectionHistoryFilter, endDate: e.target.value }); setCollectionHistoryPage(1); }} /></div>
                  <div><Label style={{ color: t3 }}>Search</Label>
                    <Input type="text" placeholder="OR #, SI #, Amount..." value={collectionHistoryFilter.searchQuery}
                      onChange={(e) => setCollectionHistoryFilter({ ...collectionHistoryFilter, searchQuery: e.target.value })} /></div>
                </div>
              </div>
              {collections.length === 0 ? (
                <div className="text-center py-16">
                  <DollarSign className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                  <h3 className="text-lg font-medium mb-2" style={{ color: t1 }}>No Collection Records Yet</h3>
                  <p style={{ color: t3 }}>Payments and collections will appear here once recorded.</p>
                </div>
              ) : (() => {
                const filtered = collections.filter(c => {
                  if (collectionHistoryFilter.startDate) {
                    const d = c.createdAt ? new Date(c.createdAt) : new Date(0);
                    if (d < new Date(collectionHistoryFilter.startDate)) return false;
                  }
                  if (collectionHistoryFilter.endDate) {
                    const d = c.createdAt ? new Date(c.createdAt) : new Date(0);
                    const endDay = new Date(collectionHistoryFilter.endDate); endDay.setHours(23, 59, 59, 999);
                    if (d > endDay) return false;
                  }
                  if (collectionHistoryFilter.searchQuery) {
                    const q = collectionHistoryFilter.searchQuery.toLowerCase();
                    const haystack = `${c.orNumber || ''} ${c.salesInvoiceRef || ''} ${c.totalAmount || ''} ${c.payer || ''}`.toLowerCase();
                    if (!haystack.includes(q)) return false;
                  }
                  return true;
                });
                const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
                const startIdx = (collectionHistoryPage - 1) * ITEMS_PER_PAGE;
                const pageRecords = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE);
                const totalCollected = filtered.reduce((s, c) => s + (c.totalAmount || 0), 0);
                return (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(34,197,94,0.04))', border: '1px solid rgba(34,197,94,0.3)' }}>
                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgb(22,101,52)' }}>Total Collected</p>
                        <p className="mt-2 text-3xl font-bold" style={{ color: 'rgb(21,128,61)' }}>₱{totalCollected.toFixed(2)}</p>
                      </div>
                      <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(59,130,246,0.04))', border: '1px solid rgba(59,130,246,0.3)' }}>
                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgb(30,64,175)' }}>Transactions</p>
                        <p className="mt-2 text-3xl font-bold" style={{ color: 'rgb(37,99,235)' }}>{filtered.length}</p>
                      </div>
                      <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.14), rgba(249,115,22,0.04))', border: '1px solid rgba(249,115,22,0.3)' }}>
                        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgb(154,52,18)' }}>Pending Count</p>
                        <p className="mt-2 text-3xl font-bold" style={{ color: 'rgb(234,88,12)' }}>{filtered.filter(c => !c.isFullyPaid).length}</p>
                      </div>
                    </div>
                    <ScrollArea className="max-h-[400px] rounded-lg border" style={{ borderColor: contentBorder }}>
                      <Table>
                        <TableHeader><TableRow style={{ background: s1 }}>
                          <TableHead style={{ color: t1 }}>OR #</TableHead>
                          <TableHead style={{ color: t1 }}>Date</TableHead>
                          <TableHead style={{ color: t1 }}>SI Ref</TableHead>
                          <TableHead style={{ color: t1 }}>Payer</TableHead>
                          <TableHead style={{ color: t1 }} className="text-right">Amount (₱)</TableHead>
                          <TableHead style={{ color: t1 }}>Status</TableHead>
                          <TableHead style={{ color: t1 }} className="text-right">Actions</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {pageRecords.map((c, idx) => (
                            <TableRow key={c.id || idx}>
                              <TableCell style={{ color: t1, fontWeight: 600 }}>{c.orNumber || `OR-${c.id}`}</TableCell>
                              <TableCell style={{ color: t3 }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '--/--/--'}</TableCell>
                              <TableCell style={{ color: t3 }}>{c.salesInvoiceRef || '--'}</TableCell>
                              <TableCell style={{ color: t1 }}>{c.payer || '--'}</TableCell>
                              <TableCell style={{ color: t1, fontWeight: 700, textAlign: 'right' }}>₱{(c.totalAmount || 0).toFixed(2)}</TableCell>
                              <TableCell><Badge className={c.isFullyPaid ? 'bg-green-100 text-green-800' : c.isPartial ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
                                {c.isFullyPaid ? 'Paid' : c.isPartial ? 'Partial' : 'Pending'}
                              </Badge></TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedCollectionRecord(c); setIsCollectionHistoryPreviewOpen(true); }}><FileText className="h-3 w-3 mr-1" /> View</Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          {pageRecords.length === 0 && (
                            <TableRow><TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: t4 }}>No collection records match the filters.</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                    {totalPages > 1 && (
                      <div className="flex justify-between items-center mt-4">
                        <p className="text-sm" style={{ color: t4 }}>Showing {startIdx + 1}-{Math.min(startIdx + ITEMS_PER_PAGE, filtered.length)} of {filtered.length}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={collectionHistoryPage === 1} onClick={() => setCollectionHistoryPage(p => Math.max(1, p - 1))}>Prev</Button>
                          <Button variant="outline" size="sm" disabled={collectionHistoryPage === totalPages} onClick={() => setCollectionHistoryPage(p => Math.min(totalPages, p + 1))}>Next</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* ===== TAB 6: SETTINGS ===== */}
        {projectDetailTab === 'settings' && (
          <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
            <CardHeader>
              <CardTitle style={{ color: t1 }}>Project Settings</CardTitle>
              <CardDescription style={{ color: t3 }}>Manage project tasks and configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: t1 }}>Project Tasks</h3>
                <div className="flex justify-between items-center mb-4">
                  <p className="text-sm" style={{ color: t3 }}>Create and manage project tasks with calendar attendance tracking.</p>
                  {!isAddingTask && (
                    <Button size="sm" style={{ background: b2 }} onClick={() => setIsAddingTask(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Add Task
                    </Button>
                  )}
                </div>
                {isAddingTask && (
                  <div className="mb-6 p-4 rounded-lg border-2 border-dashed" style={{ borderColor: contentBorder, background: s1 }}>
                    <h4 className="font-semibold mb-4" style={{ color: t1 }}>New Task</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div><Label style={{ color: t1 }}>Task Name *</Label>
                        <Input value={newTask.task_name} onChange={(e) => setNewTask({ ...newTask, task_name: e.target.value })} placeholder="Enter task name" /></div>
                      <div><Label style={{ color: t1 }}>Assigned Resources</Label>
                        <Input value={newTask.assigned_resources} onChange={(e) => setNewTask({ ...newTask, assigned_resources: e.target.value })} placeholder="Person / team" /></div>
                      <div><Label style={{ color: t1 }}>Start Date *</Label>
                        <Input type="date" value={newTask.start_date} onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })} /></div>
                      <div><Label style={{ color: t1 }}>End Date *</Label>
                        <Input type="date" value={newTask.end_date} onChange={(e) => setNewTask({ ...newTask, end_date: e.target.value })} /></div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => { setIsAddingTask(false); setNewTask({ task_name: '', assigned_resources: '', start_date: '', end_date: '' }); }}>Cancel</Button>
                      <Button style={{ background: b2 }} onClick={async () => {
                        if (!newTask.task_name || !newTask.start_date || !newTask.end_date) { toast.error('Please fill in Task Name, Start Date, and End Date'); return; }
                        try {
                          await createProjectTask(selectedProject.id, newTask);
                          setNewTask({ task_name: '', assigned_resources: '', start_date: '', end_date: '' });
                          setIsAddingTask(false);
                        } catch { /* toast already shown */ }
                      }}><Save className="h-4 w-4 mr-2" /> Save Task</Button>
                    </div>
                  </div>
                )}
                {projectTasks.length === 0 && !isAddingTask ? (
                  <div className="text-center py-12 rounded-lg border" style={{ borderColor: contentBorder }}>
                    <Calendar className="h-12 w-12 mx-auto mb-3" style={{ color: t4, opacity: 0.5 }} />
                    <p style={{ color: t3 }}>No tasks yet. Click "Add Task" to create the first one.</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[400px] rounded-lg border" style={{ borderColor: contentBorder }}>
                    <Table>
                      <TableHeader><TableRow style={{ background: s1 }}>
                        <TableHead style={{ color: t1 }}>Task</TableHead>
                        <TableHead style={{ color: t1 }}>Assigned</TableHead>
                        <TableHead style={{ color: t1 }}>Date Range</TableHead>
                        <TableHead style={{ color: t1 }}>Progress</TableHead>
                        <TableHead style={{ color: t1 }}>Status</TableHead>
                        <TableHead style={{ color: t1 }} className="text-right">Actions</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {projectTasks.map(task => {
                          const completion = calculateTaskCompletion(task);
                          const status = calculateTaskStatus(task);
                          return (
                            <TableRow key={task.id}>
                              <TableCell style={{ color: t1, fontWeight: 500 }}>{task.task_name}</TableCell>
                              <TableCell style={{ color: t3 }}>{task.assigned_resources || '--'}</TableCell>
                              <TableCell style={{ color: t3, fontSize: '0.85rem' }}>
                                {task.start_date ? new Date(task.start_date).toLocaleDateString() : '--'} → {task.end_date ? new Date(task.end_date).toLocaleDateString() : '--'}
                              </TableCell>
                              <TableCell className="min-w-[160px]">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: s1 }}>
                                    <div className={cn('h-full rounded-full', completion >= 75 ? 'bg-green-600' : completion >= 50 ? 'bg-blue-600' : completion >= 25 ? 'bg-orange-600' : 'bg-gray-500')} style={{ width: `${completion}%` }} />
                                  </div>
                                  <span className="text-xs font-semibold whitespace-nowrap" style={{ color: t1 }}>{completion}%</span>
                                </div>
                              </TableCell>
                              <TableCell><Badge className={status === 'Completed' ? 'bg-green-100 text-green-800' : status === 'In Progress' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>{status}</Badge></TableCell>
                              <TableCell className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedTaskForCalendar(task); }}>
                                    <CalendarIcon className="h-3 w-3 mr-1" /> Attendance
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteProjectTask(selectedProject.id, task.id)}>
                                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </div>
              <div className="pt-6 border-t" style={{ borderColor: contentBorder }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: t1 }}>Project Information</h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => handleEditProject(selectedProject)}><Edit className="h-4 w-4 mr-2" /> Edit Project Info</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    );
  ;

  const renderProjectListView = () => {
    const currentDateTime = new Date().toLocaleString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: t1 }}>MPS - Project Management</h2>
            <p style={{ color: t3 }}>Create and manage all projects</p>
          </div>
          <div className="text-sm" style={{ color: t4 }}>{currentDateTime}</div>
        </div>
        <div className="flex justify-start">
          <Button onClick={() => setIsCreateProjectModalOpen(true)} style={{ background: b2 }} size="lg">
            <Plus className="h-5 w-5 mr-2" /> Create New Project
          </Button>
        </div>
        <Card className="shadow-lg border-0" style={{ background: contentBg, border: `1px solid ${contentBorder}` }}>
          <CardHeader>
            <CardTitle style={{ color: t1 }}>All Projects</CardTitle>
            <CardDescription style={{ color: t3 }}>Manage and monitor all your projects</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-[600px]">
              {projects.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 mx-auto mb-4" style={{ color: t4, opacity: 0.5 }} />
                  <p style={{ color: t3 }}>No projects yet. Create your first project to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {projects.map((project) => (
                    <Card3D
                      key={project.id}
                      intensity={7}
                      lift={16}
                      className="rounded-xl overflow-hidden cursor-pointer card-3d-project-tile"
                    >
                      <div
                        onClick={() => handleProjectClick(project)}
                        style={{ background: contentBg, border: `1px solid ${contentBorder}`, minHeight: '100%' }}
                        className="h-full transition-all"
                      >
                        <Card className="shadow-none border-0 bg-transparent">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-3">
                              <h3 className="font-semibold text-lg line-clamp-1" style={{ color: t1 }}>
                                {project.title || project.name}
                              </h3>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleEditProject(project); }}
                                  className="h-6 w-6 p-0">
                                  <Edit className="h-4 w-4" style={{ color: 'var(--nad-info, #3b82f6)' }} />
                                </Button>
                                <Button variant="ghost" size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id); }}
                                  className="h-6 w-6 p-0">
                                  <Trash2 className="h-4 w-4" style={{ color: '#ef4444' }} />
                                </Button>
                              </div>
                            </div>
                            {project.accountName && (
                              <p className="text-sm mb-2" style={{ color: t3 }}>
                                <span className="font-medium">Account:</span> {project.accountName}
                              </p>
                            )}
                            {project.companyAddress && (
                              <p className="text-xs mb-2 line-clamp-2" style={{ color: t3 }}>
                                📍 {project.companyAddress}
                              </p>
                            )}
                            <div className="space-y-2 mt-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs" style={{ color: t4 }}>Status:</span>
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                  project.status === 'Complete' ? 'bg-blue-100 text-blue-800' :
                                  project.status === 'Running' ? 'bg-green-100 text-green-800' :
                                  project.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{project.status}</span>
                              </div>
                              {project.contactPersons && project.contactPersons.filter(p => p).length > 0 && (
                                <div className="text-xs" style={{ color: t3 }}>
                                  <span className="font-medium">Contact:</span> {project.contactPersons.filter(p => p)[0]}
                                  {project.contactPersons.filter(p => p).length > 1 && (
                                    <span style={{ color: 'var(--nad-info, #6366f1)' }}> +{project.contactPersons.filter(p => p).length - 1} more</span>
                                  )}
                                </div>
                              )}
                              {project.contactDetails && project.contactDetails.filter(d => d).length > 0 && (
                                <div className="text-xs" style={{ color: t3 }}>📞 {project.contactDetails.filter(d => d)[0]}</div>
                              )}
                              {project.emailAddresses && project.emailAddresses.filter(e => e).length > 0 && (
                                <div className="text-xs truncate" style={{ color: t3 }}>✉️ {project.emailAddresses.filter(e => e)[0]}</div>
                              )}
                              {project.startDate && (
                                <div className="flex items-center justify-between text-xs pt-2 border-t" style={{ borderColor: contentBorder, color: t4 }}>
                                  <span>Start:</span>
                                  <span>{new Date(project.startDate).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </Card3D>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTaskAttendanceModal = () => {
    if (!selectedTaskForCalendar) return null;
    const dateRange = getTaskDateRange(selectedTaskForCalendar);
    const attended = taskAttendedDates[selectedTaskForCalendar.id] || [];
    return (
      <Dialog open={!!selectedTaskForCalendar} onOpenChange={(open) => !open && setSelectedTaskForCalendar(null)}>
        <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-hidden flex flex-col nad-modal-content">
          <DialogHeader>
            <DialogTitle style={{ color: t1 }}>Task Attendance: {selectedTaskForCalendar.task_name}</DialogTitle>
            <DialogDescription style={{ color: t3 }}>Toggle dates to mark attendance. {dateRange.length} total days.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 max-h-[400px] px-1">
            <div className="grid grid-cols-2 gap-2 py-2">
              {dateRange.map(d => {
                const isoDate = typeof d === 'string' ? d : d.toISOString().split('T')[0];
                const isAttended = attended.includes(isoDate);
                return (
                  <div key={isoDate}
                    onClick={() => toggleTaskDate(selectedTaskForCalendar.id, isoDate)}
                    className={`cursor-pointer rounded-lg border p-3 flex items-center justify-between transition-all ${isAttended ? 'bg-green-50 border-green-300' : ''}`}
                    style={{
                      background: isAttended ? 'rgba(34,197,94,0.1)' : 'transparent',
                      borderColor: isAttended ? 'rgba(34,197,94,0.4)' : contentBorder
                    }}>
                    <span className="text-sm font-medium" style={{ color: t1 }}>
                      {new Date(isoDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <Switch checked={isAttended} onCheckedChange={() => toggleTaskDate(selectedTaskForCalendar.id, isoDate)} />
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter className="pt-4 border-t flex justify-between" style={{ borderColor: contentBorder }}>
            <div className="text-sm" style={{ color: t3 }}>
              <CheckCircle className="inline h-4 w-4 mr-1" style={{ color: '#22c55e' }} />
              {attended.length}/{dateRange.length} days marked
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSelectedTaskForCalendar(null)}>Close</Button>
              <Button style={{ background: b2 }} onClick={async () => {
                try {
                  await saveTaskAttendance(selectedTaskForCalendar.id);
                  setSelectedTaskForCalendar(null);
                } catch { /* toast already shown */ }
              }}><Save className="h-4 w-4 mr-2" /> Save Attendance</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  const renderBatchReadingModal = () => (
    <Dialog open={isBatchReadingModalOpen} onOpenChange={setIsBatchReadingModalOpen}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Batch Meter Readings</DialogTitle>
          <DialogDescription style={{ color: t3 }}>Edit multiple readings at once. {selectedBatchReadings.length} items selected.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[450px]">
          {selectedBatchReadings.length === 0 ? (
            <div className="text-center py-12">
              <Box className="h-12 w-12 mx-auto mb-3" style={{ color: t4, opacity: 0.5 }} />
              <p style={{ color: t3 }}>No readings selected for batch editing.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow style={{ background: s1 }}>
                <TableHead style={{ color: t1 }}>Type</TableHead>
                <TableHead style={{ color: t1 }}>Present Read</TableHead>
                <TableHead style={{ color: t1 }}>Spoilage %</TableHead>
                <TableHead style={{ color: t1 }}>Rate</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {selectedBatchReadings.map(r => {
                  const edited = editableBatchReadings[r.id] || r;
                  return (
                    <TableRow key={r.id}>
                      <TableCell style={{ color: t1, fontWeight: 600 }}>{r.type}</TableCell>
                      <TableCell>
                        <Input type="number" value={edited.presentReading ?? ''}
                          onChange={e => setEditableBatchReadings(prev => ({
                            ...prev, [r.id]: { ...(prev[r.id] || r), presentReading: parseFloat(e.target.value) || 0 }
                          }))} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={edited.spoilageTolerance ?? ''}
                          onChange={e => setEditableBatchReadings(prev => ({
                            ...prev, [r.id]: { ...(prev[r.id] || r), spoilageTolerance: parseFloat(e.target.value) || 0 }
                          }))} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={edited.rate ?? ''}
                          onChange={e => setEditableBatchReadings(prev => ({
                            ...prev, [r.id]: { ...(prev[r.id] || r), rate: parseFloat(e.target.value) || 0 }
                          }))} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => { setIsBatchReadingModalOpen(false); setEditableBatchReadings({}); }}>Cancel</Button>
          <Button style={{ background: b2 }} onClick={async () => {
            try {
              await saveBatchReadings();
              setIsBatchReadingModalOpen(false);
              setEditableBatchReadings({});
            } catch { /* toast already shown */ }
          }}><Save className="h-4 w-4 mr-2" /> Save Batch</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderBillingRecordsModal = () => (
    <Dialog open={isBillingRecordsModalOpen} onOpenChange={setIsBillingRecordsModalOpen}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>
            Billing Records — {selectedMachineForBillingRecords?.model || 'Machine'}
            {selectedMachineForBillingRecords?.departmentName && (
              <span className="text-sm font-normal ml-2" style={{ color: t3 }}>({selectedMachineForBillingRecords.departmentName})</span>
            )}
          </DialogTitle>
          <DialogDescription style={{ color: t3 }}>S/N: {selectedMachineForBillingRecords?.serialNumber || '--'} • {billingRecordsData.length} reading sessions</DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[500px]">
          {billingRecordsData.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-3" style={{ color: t4, opacity: 0.5 }} />
              <p style={{ color: t3 }}>No billing records found for this machine.</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {billingRecordsData.map((rec, idx) => (
                <Card key={idx} style={{ background: s1, border: `1px solid ${contentBorder}` }}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold" style={{ color: t1 }}>
                        📅 {new Date(rec.date + 'T00:00:00').toLocaleDateString()}
                      </h4>
                      <div className="text-right">
                        <span className="text-xs" style={{ color: t3 }}>Total Amount</span>
                        <p className="font-bold text-lg" style={{ color: t1 }}>₱{rec.totalAmount.toFixed(2)}</p>
                      </div>
                    </div>
                    <Table size="sm">
                      <TableHeader><TableRow>
                        <TableHead style={{ color: t1 }}>Type</TableHead>
                        <TableHead style={{ color: t1, textAlign: 'right' }}>Present</TableHead>
                        <TableHead style={{ color: t1, textAlign: 'right' }}>Consumed</TableHead>
                        <TableHead style={{ color: t1, textAlign: 'right' }}>Amount</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {rec.readings.map(r => (
                          <TableRow key={r.id}>
                            <TableCell style={{ color: t3, fontWeight: 500 }}>{r.type}</TableCell>
                            <TableCell style={{ color: t3, textAlign: 'right' }}>{r.presentReading || 0}</TableCell>
                            <TableCell style={{ color: t3, textAlign: 'right' }}>{r.net || 0}</TableCell>
                            <TableCell style={{ color: t1, fontWeight: 600, textAlign: 'right' }}>₱{(r.amount || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => { setIsBillingRecordsModalOpen(false); setBillingRecordsData([]); }}>Close</Button>
          <Button style={{ background: b2 }} onClick={() => {
            if (selectedMachineForBillingRecords?.id) exportDepartmentDataCSV(selectedMachineForBillingRecords.id);
          }}><FileDown className="h-4 w-4 mr-2" /> Export CSV</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderConsolidatedSIModal = () => (
    <Dialog open={isConsolidatedSIModalOpen} onOpenChange={setIsConsolidatedSIModalOpen}>
      <DialogContent className="sm:max-w-[520px] nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Consolidated Sales Invoice</DialogTitle>
          <DialogDescription style={{ color: t3 }}>{pendingSIMachines.length} machines pending SI generation.</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[300px]">
          {pendingSIMachines.length === 0 ? (
            <div className="text-center py-8" style={{ color: t3 }}>No pending machines.</div>
          ) : (
            <div className="space-y-2">
              {pendingSIMachines.map(m => (
                <div key={m.id} className="p-3 rounded-lg border flex justify-between items-center"
                  style={{ borderColor: contentBorder, background: s1 }}>
                  <div>
                    <p className="font-medium" style={{ color: t1 }}>{m.model}</p>
                    <p className="text-xs" style={{ color: t4 }}>S/N: {m.serialNumber}</p>
                  </div>
                  <Badge className={getBillingMachineColorClass(m).badge}>Pending</Badge>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => setIsConsolidatedSIModalOpen(false)}>Cancel</Button>
          <Button style={{ background: b2 }} disabled={isGeneratingAllSI || pendingSIMachines.length === 0}
            onClick={async () => {
              setIsGeneratingAllSI(true);
              console.info(`[MPSProjects] Generating consolidated SI for ${pendingSIMachines.length} machines`);
              toast.success(`Generated ${pendingSIMachines.length} consolidated SI entries (demo)`);
              setPendingSIMachines([]);
              setIsGeneratingAllSI(false);
              setIsConsolidatedSIModalOpen(false);
            }}>
            {isGeneratingAllSI ? 'Generating…' : 'Generate All SI'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderFilterExportModal = () => (
    <Dialog open={isFilterExportModalOpen} onOpenChange={setIsFilterExportModalOpen}>
      <DialogContent className="sm:max-w-[560px] nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Filter & Export Data</DialogTitle>
          <DialogDescription style={{ color: t3 }}>Apply filters then export results to CSV.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div><Label style={{ color: t1 }}>Department</Label>
            <Input placeholder="Department name..." value={filterParams.department}
              onChange={e => setFilterParams({ ...filterParams, department: e.target.value })} /></div>
          <div><Label style={{ color: t1 }}>Machine Model</Label>
            <Input placeholder="e.g. MP2555" value={filterParams.machine}
              onChange={e => setFilterParams({ ...filterParams, machine: e.target.value })} /></div>
          <div><Label style={{ color: t1 }}>Serial #</Label>
            <Input placeholder="Serial number..." value={filterParams.serial}
              onChange={e => setFilterParams({ ...filterParams, serial: e.target.value })} /></div>
          <div><Label style={{ color: t1 }}>Date (YYYY-MM-DD)</Label>
            <Input type="date" value={filterParams.date}
              onChange={e => setFilterParams({ ...filterParams, date: e.target.value })} /></div>
          <div className="col-span-2"><Label style={{ color: t1 }}>Ticket #</Label>
            <Input placeholder="Ticket number..." value={filterParams.ticketNumber}
              onChange={e => setFilterParams({ ...filterParams, ticketNumber: e.target.value })} /></div>
        </div>
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => setIsFilterExportModalOpen(false)}>Cancel</Button>
          <Button style={{ background: b2 }} onClick={() => {
            console.info('[MPSProjects] Filter export params:', filterParams);
            toast.success('Filter applied (demo). Check console for params.');
            setIsFilterExportModalOpen(false);
          }}><Filter className="h-4 w-4 mr-2" /> Apply & Export</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderOverviewModals = () => (
    <>
      <Dialog open={isDepartmentsModalOpen} onOpenChange={setIsDepartmentsModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col nad-modal-content">
          <DialogHeader><DialogTitle style={{ color: t1 }}>Departments Overview</DialogTitle></DialogHeader>
          <ScrollArea className="flex-1">
            {departments.length === 0 ? (
              <div className="text-center py-12" style={{ color: t3 }}>No departments loaded.</div>
            ) : (
              <Table>
                <TableHeader><TableRow style={{ background: s1 }}>
                  <TableHead style={{ color: t1 }}>Name</TableHead>
                  <TableHead style={{ color: t1 }}>Contact</TableHead>
                  <TableHead style={{ color: t1, textAlign: 'right' }}>Machines</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {departments.map(d => (
                    <TableRow key={d.id}>
                      <TableCell style={{ color: t1, fontWeight: 500 }}>{d.name}</TableCell>
                      <TableCell style={{ color: t3 }}>{d.contact_person || '--'}</TableCell>
                      <TableCell style={{ color: t1, textAlign: 'right', fontWeight: 600 }}>
                        {Array.isArray(d.machines) ? d.machines.length : 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
          <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
            <Button variant="outline" onClick={() => setIsDepartmentsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isMachinesModalOpen} onOpenChange={setIsMachinesModalOpen}>
        <DialogContent className="sm:max-w-[680px] max-h-[80vh] overflow-hidden flex flex-col nad-modal-content">
          <DialogHeader><DialogTitle style={{ color: t1 }}>Machines Overview</DialogTitle></DialogHeader>
          <ScrollArea className="flex-1">
            {(() => {
              const allMachines = departments.flatMap(d => (Array.isArray(d.machines) ? d.machines : []).map(m => ({ ...m, _dept: d.name })));
              if (allMachines.length === 0) return <div className="text-center py-12" style={{ color: t3 }}>No machines loaded.</div>;
              return (
                <Table>
                  <TableHeader><TableRow style={{ background: s1 }}>
                    <TableHead style={{ color: t1 }}>Model</TableHead>
                    <TableHead style={{ color: t1 }}>Serial #</TableHead>
                    <TableHead style={{ color: t1 }}>Dept.</TableHead>
                    <TableHead style={{ color: t1 }}>Installed</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {allMachines.map(m => (
                      <TableRow key={m.id}>
                        <TableCell style={{ color: t1, fontWeight: 500 }}>{m.model}</TableCell>
                        <TableCell style={{ color: t3 }}>{m.serialNumber}</TableCell>
                        <TableCell style={{ color: t3 }}>{m._dept}</TableCell>
                        <TableCell style={{ color: t3 }}>
                          {m.dateInstalled ? new Date(m.dateInstalled).toLocaleDateString() : '--'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
          </ScrollArea>
          <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
            <Button variant="outline" onClick={() => setIsMachinesModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEarningsModalOpen} onOpenChange={setIsEarningsModalOpen}>
        <DialogContent className="sm:max-w-[560px] nad-modal-content">
          <DialogHeader><DialogTitle style={{ color: t1 }}>Earnings Breakdown</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <Card style={{ background: s1, border: `1px solid ${contentBorder}` }}>
              <CardContent className="p-4 flex justify-between items-center">
                <div>
                  <p className="text-xs" style={{ color: t4 }}>Total Billing Exports</p>
                  <p className="font-bold text-2xl" style={{ color: t1 }}>₱{billingExports.reduce((s, b) => s + (b.totalAmount || 0), 0).toFixed(2)}</p>
                </div>
                <BarChart3 className="h-10 w-10" style={{ color: 'var(--nad-info, #3b82f6)' }} />
              </CardContent>
            </Card>
            <Card style={{ background: s1, border: `1px solid ${contentBorder}` }}>
              <CardContent className="p-4 flex justify-between items-center">
                <div>
                  <p className="text-xs" style={{ color: t4 }}>Total Sales Invoiced</p>
                  <p className="font-bold text-2xl" style={{ color: t1 }}>₱{siExports.reduce((s, i) => s + (i.totalAmount || 0), 0).toFixed(2)}</p>
                </div>
                <DollarSign className="h-10 w-10" style={{ color: 'var(--nad-success, #22c55e)' }} />
              </CardContent>
            </Card>
            <Card style={{ background: s1, border: `1px solid ${contentBorder}` }}>
              <CardContent className="p-4 flex justify-between items-center">
                <div>
                  <p className="text-xs" style={{ color: t4 }}>Total Collected</p>
                  <p className="font-bold text-2xl" style={{ color: t1 }}>₱{collections.reduce((s, c) => s + (c.totalAmount || 0), 0).toFixed(2)}</p>
                </div>
                <CheckCircle className="h-10 w-10" style={{ color: 'var(--nad-s1, #eab308)' }} />
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
            <Button variant="outline" onClick={() => setIsEarningsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  const renderSIPreviewModal = () => (
    <Dialog open={isSIPreviewOpen} onOpenChange={setIsSIPreviewOpen}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-hidden flex flex-col nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Sales Invoice Preview</DialogTitle>
          <DialogDescription style={{ color: t3 }}>
            {selectedSIData ? `SI #${selectedSIData.siNumber || selectedSIData.id || ''}` : ''}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1">
          {selectedSIData ? (
            <Card className="my-2" style={{ background: '#ffffff', border: `1px solid ${contentBorder}` }}>
              <CardContent className="p-8 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-xl text-gray-900">SALES INVOICE</h3>
                    <p className="text-sm text-gray-600">SI #: {selectedSIData.siNumber || `SI-${selectedSIData.id}`}</p>
                    <p className="text-sm text-gray-600">Date: {selectedSIData.createdAt ? new Date(selectedSIData.createdAt).toLocaleDateString() : '--'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{selectedProject?.accountName || ''}</p>
                    <p className="text-sm text-gray-600">{selectedProject?.companyAddress || ''}</p>
                  </div>
                </div>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="bg-gray-100 text-gray-900">Description</TableHead>
                    <TableHead className="bg-gray-100 text-gray-900 text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-gray-800">{selectedSIData.description || 'Metered services'}</TableCell>
                      <TableCell className="text-gray-900 font-semibold text-right">₱{(selectedSIData.totalAmount || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="pt-4 border-t text-right">
                  <p className="text-xs text-gray-600">Total Amount Due</p>
                  <p className="text-3xl font-bold text-gray-900">₱{(selectedSIData.totalAmount || 0).toFixed(2)}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12" style={{ color: t3 }}>No SI data loaded.</div>
          )}
        </ScrollArea>
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => setIsSIPreviewOpen(false)}>Close</Button>
          <Button style={{ background: b2 }} onClick={() => toast.success('SI export triggered (demo)')}>
            <Printer className="h-4 w-4 mr-2" /> Print / Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderCollectionPreviewModal = () => (
    <Dialog open={isCollectionHistoryPreviewOpen} onOpenChange={setIsCollectionHistoryPreviewOpen}>
      <DialogContent className="sm:max-w-[520px] nad-modal-content">
        <DialogHeader>
          <DialogTitle style={{ color: t1 }}>Collection Details</DialogTitle>
          <DialogDescription style={{ color: t3 }}>OR receipt & payment status</DialogDescription>
        </DialogHeader>
        {selectedCollectionRecord && (
          <div className="space-y-4 py-4">
            <Card style={{ background: s1, border: `1px solid ${contentBorder}` }}>
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between"><span style={{ color: t4 }}>OR Number</span>
                  <span style={{ color: t1, fontWeight: 700 }}>{selectedCollectionRecord.orNumber || `OR-${selectedCollectionRecord.id}`}</span></div>
                <div className="flex justify-between"><span style={{ color: t4 }}>Date Collected</span>
                  <span style={{ color: t1 }}>{selectedCollectionRecord.createdAt ? new Date(selectedCollectionRecord.createdAt).toLocaleDateString() : '--'}</span></div>
                <div className="flex justify-between"><span style={{ color: t4 }}>SI Reference</span>
                  <span style={{ color: t1 }}>{selectedCollectionRecord.salesInvoiceRef || '--'}</span></div>
                <div className="flex justify-between"><span style={{ color: t4 }}>Payer</span>
                  <span style={{ color: t1 }}>{selectedCollectionRecord.payer || '--'}</span></div>
                <div className="flex justify-between pt-2 border-t" style={{ borderColor: contentBorder }}>
                  <span style={{ color: t4, fontWeight: 600 }}>Total Amount</span>
                  <span style={{ color: t1, fontWeight: 800, fontSize: '1.25rem' }}>₱{(selectedCollectionRecord.totalAmount || 0).toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label style={{ color: t1 }}>Payment Status</Label>
                {!isEditingCollectionStatus && (
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditedCollectionStatus(
                      selectedCollectionRecord.isFullyPaid ? 'Paid' : selectedCollectionRecord.isPartial ? 'Partial' : 'Pending'
                    );
                    setIsEditingCollectionStatus(true);
                  }}><Edit className="h-3 w-3 mr-1" /> Edit</Button>
                )}
              </div>
              {isEditingCollectionStatus ? (
                <div className="flex gap-2">
                  <Select value={editedCollectionStatus} onValueChange={setEditedCollectionStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent style={{ background: contentBg }}>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button style={{ background: b2 }} onClick={async () => {
                    try {
                      const statusBody = {
                        isFullyPaid: editedCollectionStatus === 'Paid',
                        isPartial: editedCollectionStatus === 'Partial'
                      };
                      await axiosInstance.put(`/collections/${selectedCollectionRecord.id}`, statusBody);
                      toast.success('Collection status updated');
                      setIsEditingCollectionStatus(false);
                      if (selectedProject?.id) await fetchCollections(selectedProject.id);
                    } catch (err) {
                      console.warn('[MPSProjects] Collection status update failed:', err?.message || err);
                      toast.error('Failed to update status');
                    }
                  }}><Save className="h-4 w-4 mr-2" /> Save</Button>
                  <Button variant="outline" onClick={() => setIsEditingCollectionStatus(false)}>Cancel</Button>
                </div>
              ) : (
                <Badge className={
                  selectedCollectionRecord.isFullyPaid ? 'bg-green-100 text-green-800' :
                  selectedCollectionRecord.isPartial ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }>
                  {selectedCollectionRecord.isFullyPaid ? 'Fully Paid' : selectedCollectionRecord.isPartial ? 'Partial Payment' : 'Pending Payment'}
                </Badge>
              )}
            </div>
            {selectedCollectionRecord.notes && (
              <div>
                <Label style={{ color: t1 }}>Notes</Label>
                <p className="text-sm mt-1" style={{ color: t3 }}>{selectedCollectionRecord.notes}</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="pt-4 border-t" style={{ borderColor: contentBorder }}>
          <Button variant="outline" onClick={() => { setIsCollectionHistoryPreviewOpen(false); setIsEditingCollectionStatus(false); }}>Close</Button>
          <Button style={{ background: b2 }} onClick={() => toast.success('OR print triggered (demo)')}>
            <Printer className="h-4 w-4 mr-2" /> Print OR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <TooltipProvider>
      <div className="w-full h-full" style={{ minHeight: 'calc(100vh - 24px)' }}>
        {selectedProject ? renderProjectDetailView() : renderProjectListView()}
      </div>
      {renderCreateProjectModal()}
      {renderEditProjectModal()}
      {renderDepartmentModals()}
      {renderMeterReadingModals()}
      {renderTaskAttendanceModal()}
      {renderBatchReadingModal()}
      {renderBillingRecordsModal()}
      {renderConsolidatedSIModal()}
      {renderFilterExportModal()}
      {renderOverviewModals()}
      {renderSIPreviewModal()}
      {renderCollectionPreviewModal()}
    </TooltipProvider>
  );
}

export default MPSProjects;
